import { useMemo, useRef, useEffect, useCallback, useState } from "react";
import { MASTER_PRINT_FONT_BASE_URL, getFontById, getFontFaceUrl, getFontScale } from "../../utils/designerUtils";
import { drawShapedText } from "../../utils/drawShapedText";
import { coordToPct, resolvePlacementSide, LEGACY_BASE_SIZE } from "../../utils/designerGeometry";
import {
  applyPredesignSizeAttrs,
  PRINT_SCALE_MAX,
  PRINT_SCALE_MIN,
  getSelectionEffectiveDimensionsCm,
  getPrintInstanceOrder,
  isFixedPredesignSelection,
  isTintablePrint,
  roundUpToHalfCm,
  sanitizePrintLocationLabel,
} from "../../utils/designerHelpers";
import {
  buildCustomCutRuntime,
  createRelativeCustomCutFromStagePoints,
  drawCustomCutRuntime,
  drawCustomCutStroke,
  findNearestCustomCutAtPoint,
  normalizeCustomCutStroke,
  resolveCustomCutRuntimeComponent,
  translateRelativeCustomCut,
} from "../../utils/designerCustomCuts";
import styles from "./Designer.module.css";

// Assets del diseñador servidos por V4 (ver designerHelpers.js).
const CDN_BASE = process.env.REACT_APP_ASSET_URL || "https://v4.micuenta.blue";
// Escala VERTICAL del print: el TAMAÑO de un print es su ALTO (regla del negocio). La espalda
// talle 4 (~60 cm) se dibuja ~660 px de un lienzo de ~900 → ~11 px/cm ⇒ 900 px ≈ 81,8 cm de alto.
// Un print de 24,5 cm ocupa 24,5/81,8 ≈ 30% del alto del stage ≈ 42% del alto de la espalda.
const PRINT_SCALE_CM_H = 81.8;
const DEFAULT_PRINT_BASE_SIZE_CM = 4;
const SCALE_MIN = PRINT_SCALE_MIN;
const SCALE_MAX = PRINT_SCALE_MAX;
const PRINT_CONSTRAINT_INSET_PX = 6;

// Ubica el popover de edición de tiras sin que se salga del borde: se alinea a la izquierda,
// centro o derecha del ancla según dónde esté la tira, y arriba/abajo si está muy alta.
const stripePopoverPlacement = (anchor) => {
  const ax = Math.max(6, Math.min(94, Number(anchor?.[0] || 0) * 100));
  const ay = Math.max(4, Math.min(96, Number(anchor?.[1] || 0) * 100));
  const tx = ax < 30 ? "0%" : (ax > 70 ? "-100%" : "-50%");
  const below = ay < 22;
  return { left: `${ax}%`, top: `${ay}%`, transform: `translate(${tx}, ${below ? "10px" : "-110%"})` };
};

const comparePrintStackOrder = (aEntry, bEntry, activePrintKey = null) => {
  const [aKey, aSelection] = aEntry;
  const [bKey, bSelection] = bEntry;
  const activeKey = String(activePrintKey || "");
  const aIsActive = activeKey && String(aKey) === activeKey;
  const bIsActive = activeKey && String(bKey) === activeKey;
  if (aIsActive !== bIsActive) return aIsActive ? 1 : -1;
  const aOrder = getPrintInstanceOrder(aKey, aSelection);
  const bOrder = getPrintInstanceOrder(bKey, bSelection);
  if (aOrder !== bOrder) return aOrder - bOrder;
  return String(aKey).localeCompare(String(bKey));
};

const LEGACY_PRINT_MAP = {
  1: { top: 359, left: 530, maxw: 15 },
  2: { top: 424, left: 530, maxw: 15 },
  3: { top: 697, left: 597, maxw: 15 },
  4: { top: 359, left: 320, maxw: 15 },
  5: { top: 424, left: 320, maxw: 15 },
  6: { top: 697, left: 249, maxw: 15 },
  7: { top: 400, left: 424, maxw: 30 },
  8: { top: 609, left: 530, maxw: 15 },
  9: { top: 704, left: 500, maxw: 15 },
  10: { top: 609, left: 320, maxw: 15 },
  11: { top: 704, left: 345, maxw: 15 },
  12: { top: 609, left: 424, maxw: 15 },
  13: { top: 704, left: 424, maxw: 15 },
  14: { top: 272, left: 424, maxw: 15 },
  15: { top: 320, left: 424, maxw: 15 },
  16: { top: 340, left: 530, maxw: 15 },
  17: { top: 340, left: 320, maxw: 15 },
  18: { top: 472, left: 424, maxw: 15 },
  19: { top: 424, left: 530, maxw: 15 },
  20: { top: 424, left: 320, maxw: 15 },
  21: { top: 704, left: 424, maxw: 15 },
  22: { top: 704, left: 530, maxw: 15 },
  23: { top: 704, left: 320, maxw: 15 },
  26: { top: 46, left: 424, maxw: 15 },
  27: { top: 329, left: 171, maxw: 15 },
  28: { top: 398, left: 141, maxw: 15 },
  29: { top: 724, left: 108, maxw: 15 },
  30: { top: 724, left: 719, maxw: 15 },
  33: { top: 724, left: 719, maxw: 15 },
  34: { top: 724, left: 108, maxw: 15 },
  35: { top: 329, left: 669, maxw: 15 },
  36: { top: 398, left: 699, maxw: 15 },
  37: { top: 420, left: 530, maxw: 15 },
  38: { top: 420, left: 320, maxw: 15 },
  39: { top: 35, left: 415, maxw: 15 },
  40: { top: 520, left: 424, maxw: 15 },
  42: { top: 200, left: 310, maxw: 15 },
  43: { top: 200, left: 505, maxw: 15 },
  44: { top: 675, left: 265, maxw: 15 },
  45: { top: 675, left: 540, maxw: 15 },
  46: { top: 175, left: 400, maxw: 15 },
  47: { top: 200, left: 310, maxw: 15 },
  48: { top: 200, left: 505, maxw: 15 },
};

// V3 se está apagando: rutas relativas de storage de V4 (master-prints/…, master-parts/…) van a V4.
const V4_FILES_BASE = (process.env.REACT_APP_V4_FILES_URL || "https://v4.micuenta.blue").replace(/\/+$/, "");
const V4_STORAGE_RE = /^\/?(storage\/)?(master-prints|master-parts|masters|predesigns|sketches)\//i;
const resolveV4Storage = (src) => `${V4_FILES_BASE}/storage/${String(src || "").replace(/^\/+/, "").replace(/^storage\//i, "")}`;

const getImgUrl = (path) => {
  if (!path) return null;
  try {
    if (path.startsWith("http")) {
      const urlObj = new URL(path);
      if (urlObj.hostname === "localhost" || urlObj.hostname === "127.0.0.1" || urlObj.hostname === "0.0.0.0") {
        return `${CDN_BASE}${urlObj.pathname}${urlObj.search}`;
      }
      return path;
    }
  } catch (e) {
  }
  if (V4_STORAGE_RE.test(path)) return resolveV4Storage(path);
  return `${CDN_BASE}${path.startsWith("/") ? "" : "/"}${path}`;
};

const posStyle = () => ({
  position: "absolute",
  inset: 0,
  transform: "translate(0, 0)",
  width: "100%",
  height: "100%",
});

const drawContainImage = (ctx, img, targetWidth, targetHeight) => {
  if (!ctx || !img || !targetWidth || !targetHeight) return;
  const srcWidth = img.naturalWidth || img.width || 1;
  const srcHeight = img.naturalHeight || img.height || 1;
  const scale = Math.min(targetWidth / srcWidth, targetHeight / srcHeight);
  const drawWidth = srcWidth * scale;
  const drawHeight = srcHeight * scale;
  const dx = (targetWidth - drawWidth) / 2;
  const dy = (targetHeight - drawHeight) / 2;
  ctx.drawImage(img, dx, dy, drawWidth, drawHeight);
};

const drawEmbroideryText = ({
  ctx,
  lines,
  lineHeight,
  fillColor,
  strokeColor,
  textLayout,
}) => {
  if (!ctx || !Array.isArray(lines)) return;
  const outlineWidth = Math.max(1, Number(ctx?.font?.match?.(/(\d+)px/)?.[1] || 16) * 0.12);
  ctx.lineJoin = "round";
  ctx.lineCap = "round";
  ctx.miterLimit = 2;
  ctx.lineWidth = outlineWidth;
  ctx.strokeStyle = strokeColor || fillColor || "#1f2a3d";
  ctx.fillStyle = fillColor || "#ffffff";

  lines.forEach((line, idx) => {
    const textY = (idx - (lines.length - 1) / 2) * lineHeight;
    const safeLine = String(line || " ");
    if (textLayout === "vertical") {
      const chars = Array.from(safeLine).filter(Boolean);
      if (!chars.length) {
        ctx.strokeText(" ", 0, textY);
        ctx.fillText(" ", 0, textY);
        return;
      }
      chars.forEach((char) => {
        ctx.strokeText(char, 0, textY);
        ctx.fillText(char, 0, textY);
      });
      return;
    }
    ctx.strokeText(safeLine, 0, textY);
    ctx.fillText(safeLine, 0, textY);
  });
};

const drawEmbroideryTintedPredesign = (ctx, image, drawW, drawH, fillColor, strokeColor, sourceRect = null) => {
  if (!ctx || !image) return;
  const off = document.createElement("canvas");
  off.width = Math.max(1, Math.round(drawW));
  off.height = Math.max(1, Math.round(drawH));
  const offCtx = off.getContext("2d");
  if (!offCtx) return;

  const fillCanvas = document.createElement("canvas");
  fillCanvas.width = off.width;
  fillCanvas.height = off.height;
  const fillCtx = fillCanvas.getContext("2d");
  if (!fillCtx) return;
  const sx = Number(sourceRect?.x || 0);
  const sy = Number(sourceRect?.y || 0);
  const sw = Math.max(1, Number(sourceRect?.width || image.naturalWidth || image.width || 1));
  const sh = Math.max(1, Number(sourceRect?.height || image.naturalHeight || image.height || 1));
  fillCtx.drawImage(image, sx, sy, sw, sh, 0, 0, off.width, off.height);
  fillCtx.globalCompositeOperation = "source-in";
  fillCtx.fillStyle = fillColor || "#ffffff";
  fillCtx.fillRect(0, 0, off.width, off.height);
  fillCtx.globalCompositeOperation = "source-over";

  const strokeCanvas = document.createElement("canvas");
  strokeCanvas.width = off.width;
  strokeCanvas.height = off.height;
  const strokeCtx = strokeCanvas.getContext("2d");
  if (!strokeCtx) return;

  const thickness = Math.max(1, Math.round(Math.max(off.width, off.height) * 0.025));
  const strokeSource = document.createElement("canvas");
  strokeSource.width = off.width;
  strokeSource.height = off.height;
  const strokeSourceCtx = strokeSource.getContext("2d");
  if (!strokeSourceCtx) return;
  strokeSourceCtx.drawImage(image, sx, sy, sw, sh, 0, 0, off.width, off.height);
  strokeSourceCtx.globalCompositeOperation = "source-in";
  strokeSourceCtx.fillStyle = strokeColor || fillColor || "#1f2a3d";
  strokeSourceCtx.fillRect(0, 0, off.width, off.height);
  strokeSourceCtx.globalCompositeOperation = "source-over";

  const offsets = [
    [-thickness, 0], [thickness, 0], [0, -thickness], [0, thickness],
    [-thickness, -thickness], [-thickness, thickness], [thickness, -thickness], [thickness, thickness],
  ];
  offsets.forEach(([ox, oy]) => {
    strokeCtx.drawImage(strokeSource, ox, oy);
  });
  strokeCtx.globalCompositeOperation = "destination-out";
  strokeCtx.drawImage(fillCanvas, 0, 0);
  strokeCtx.globalCompositeOperation = "source-over";

  offCtx.clearRect(0, 0, off.width, off.height);
  offCtx.drawImage(strokeCanvas, 0, 0);
  offCtx.drawImage(fillCanvas, 0, 0);
  ctx.drawImage(off, -drawW / 2, -drawH / 2, drawW, drawH);
};

const resolvePartLabel = (part = null) => {
  const item = part?.Item || {};
  return String(
    item?.factory_name
    || item?.name
    || item?.nname
    || part?.name
    || part?.nname
    || ""
  )
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "");
};

const isStripeLikePart = (part = null) => resolvePartLabel(part).includes("tira");

const isSleeveLikePart = (part = null) => {
  const label = resolvePartLabel(part);
  return label.includes("manga") || label.includes("sleeve");
};

// Región de la prenda a la que pertenece una parte (para recortar tiras: la tira no cruza de una
// región a otra, pero sí puede cruzar recortes DENTRO de la región). Orden = de más específica a
// menos: puño/cuello/capucha/cintura antes que manga/cuerpo. El default es el cuerpo (frente o
// espalda según la vista — solo una es visible por vez). Se comprueba por prioridad al recortar.
const STRIPE_REGION_ORDER = ["puno", "cuello", "capucha", "cintura", "bolsillo", "manga", "frente", "espalda"];
const STRIPE_AVIO_FIRST = new Set([
  "cierre", "cierres", "boton", "botones", "snap", "snaps", "alamar", "alamares",
  "solapa", "gancho", "cuerina", "cordon", "arandela", "pompon", "elastico",
]);
// Region de una parte por nombre (espeja App\Support\PartRegion de V4). Devuelve una region o
// null (avio / tira = sin recorte). Fallback cuando la parte no trae `region` del arbol.
const regionForPartLabel = (label = "") => {
  const l = String(label).toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "");
  if (!l) return null;
  if (l.includes("tira") || l.includes("tejido cardigan")) return null;
  const words = l.split(/\s+/).filter(Boolean);
  const first = words[0] === "sin" && words[1] ? words[1] : (words[0] || "");
  if (STRIPE_AVIO_FIRST.has(first)) return null;
  if (l.includes("bolsillo")) return "bolsillo";
  if (l.includes("puno") || l.includes("cuff")) return "puno";
  if (l.includes("capucha") || l.includes("hood") || l.includes("oreja") || l.includes("cresta")) return "capucha";
  if (l.includes("cuello") || l.includes("collar") || l.includes("neck")) return "cuello";
  if (l.includes("cintura") || l.includes("waist")) return "cintura";
  if (l.includes("doble boca") || l.includes("antebrazo") || l.includes("parte inferior")) return "manga";
  if (l.includes("boomber") || l.includes("bomber") || l.includes("triangulo")) return "frente";
  if (l.includes("espalda") || l.includes("trasero")) return "espalda";
  if (l.includes("manga") || l.includes("sleeve")) return "manga";
  if (l.includes("frente") || l.includes("delantero") || l.includes("pecho")) return "frente";
  return "frente"; // default (cuerpo)
};
// Region efectiva: el `region` explicito del arbol manda ("none"=avio -> excluida); si no viene, cae al nombre.
const partRegion = (part = null) => {
  const explicit = part?.Item?.region ?? part?.region ?? null;
  if (explicit === "none") return null;
  if (typeof explicit === "string" && explicit) return explicit;
  return regionForPartLabel(resolvePartLabel(part));
};

const getStrokeCollisionCoverage = (collisionMap, points = []) => {
  if (!collisionMap?.alpha?.length || !Array.isArray(points) || points.length < 2) {
    return { hits: 0, samples: 0 };
  }
  const { width, height, alpha } = collisionMap;
  if (!width || !height || !alpha?.length) return { hits: 0, samples: 0 };
  const isHit = (nx, ny) => {
    const x = clamp(Math.round(Number(nx || 0) * (width - 1)), 0, width - 1);
    const y = clamp(Math.round(Number(ny || 0) * (height - 1)), 0, height - 1);
    return alpha[(y * width) + x] > 20;
  };
  let hits = 0;
  let samples = 0;
  for (let i = 1; i < points.length; i += 1) {
    const a = points[i - 1];
    const b = points[i];
    const ax = clamp(Number(a?.[0] || 0), 0, 1);
    const ay = clamp(Number(a?.[1] || 0), 0, 1);
    const bx = clamp(Number(b?.[0] || 0), 0, 1);
    const by = clamp(Number(b?.[1] || 0), 0, 1);
    const steps = Math.max(8, Math.round(Math.hypot((bx - ax) * width, (by - ay) * height) / 5));
    for (let s = 0; s <= steps; s += 1) {
      const t = s / steps;
      const nx = ax + ((bx - ax) * t);
      const ny = ay + ((by - ay) * t);
      samples += 1;
      if (isHit(nx, ny)) hits += 1;
    }
  }
  return { hits, samples };
};

const isSleeveStrokeByGeometry = ({
  points = [],
  garmentContour = null,
  width = 0,
  height = 0,
}) => {
  if (!Array.isArray(points) || points.length < 2 || width <= 1 || height <= 1) {
    return false;
  }
  const rowBounds = Array.isArray(garmentContour?.rowBounds) ? garmentContour.rowBounds : null;
  let edgeHits = 0;
  let samples = 0;
  for (let i = 1; i < points.length; i += 1) {
    const a = points[i - 1];
    const b = points[i];
    const ax = clamp(Number(a?.[0] || 0), 0, 1);
    const ay = clamp(Number(a?.[1] || 0), 0, 1);
    const bx = clamp(Number(b?.[0] || 0), 0, 1);
    const by = clamp(Number(b?.[1] || 0), 0, 1);
    const steps = Math.max(10, Math.round(Math.hypot((bx - ax) * width, (by - ay) * height) / 6));
    for (let s = 0; s <= steps; s += 1) {
      const t = s / steps;
      const nx = ax + ((bx - ax) * t);
      const ny = ay + ((by - ay) * t);
      const xPx = nx * width;
      const yPx = clamp(Math.round(ny * (height - 1)), 0, height - 1);
      let relativeX = nx;
      const row = rowBounds?.[yPx] || null;
      if (row) {
        const min = Number(row.min || 0);
        const max = Number(row.max || 0);
        const span = Math.max(1, max - min);
        relativeX = clamp((xPx - min) / span, 0, 1);
      }
      samples += 1;
      if (relativeX <= 0.24 || relativeX >= 0.76) {
        edgeHits += 1;
      }
    }
  }
  if (!samples) return false;
  return (edgeHits / samples) >= 0.84;
};

const hasBBoxStripeCollision = (collisionMap, bbox) => {
  if (!collisionMap?.alpha?.length || !bbox) return false;
  const { width, height, alpha } = collisionMap;
  if (!width || !height) return false;
  const left = clamp(Math.floor(bbox.left), 0, width - 1);
  const right = clamp(Math.ceil(bbox.right), 0, width - 1);
  const top = clamp(Math.floor(bbox.top), 0, height - 1);
  const bottom = clamp(Math.ceil(bbox.bottom), 0, height - 1);
  if (right <= left || bottom <= top) return false;
  const stepX = Math.max(1, Math.round((right - left) / 14));
  const stepY = Math.max(1, Math.round((bottom - top) / 14));
  for (let y = top; y <= bottom; y += stepY) {
    for (let x = left; x <= right; x += stepX) {
      if (alpha[(y * width) + x] > 20) return true;
    }
  }
  return false;
};

const drawStripeStroke = (ctx, stroke, widthPx, heightPx) => {
  if (!ctx || !stroke) return;
  const points = Array.isArray(stroke.points_norm) ? stroke.points_norm : [];
  if (!points.length) return;
  const thickness = Math.max(2, Number(stroke.thickness || 8));
  const opacity = clamp(Number(stroke.opacity ?? 0.94), 0.88, 1);
  ctx.save();
  ctx.globalAlpha = opacity;
  ctx.strokeStyle = String(stroke.color || "#1d4ed8");
  ctx.lineWidth = thickness;
  ctx.lineJoin = "round";
  ctx.lineCap = "round";
  ctx.beginPath();
  const first = points[0] || [0.5, 0.5];
  ctx.moveTo(clamp(Number(first[0] || 0) * widthPx, 0, widthPx), clamp(Number(first[1] || 0) * heightPx, 0, heightPx));
  if (String(stroke.tool || "").toLowerCase() === "line" && points.length >= 2) {
    const end = points[1] || first;
    ctx.lineTo(clamp(Number(end[0] || 0) * widthPx, 0, widthPx), clamp(Number(end[1] || 0) * heightPx, 0, heightPx));
  } else {
    for (let i = 1; i < points.length; i += 1) {
      const p = points[i] || points[i - 1];
      ctx.lineTo(clamp(Number(p[0] || 0) * widthPx, 0, widthPx), clamp(Number(p[1] || 0) * heightPx, 0, heightPx));
    }
  }
  ctx.stroke();
  ctx.restore();
};

const distancePointToSegment = (px, py, ax, ay, bx, by) => {
  const abx = bx - ax;
  const aby = by - ay;
  const apx = px - ax;
  const apy = py - ay;
  const abLenSq = (abx * abx) + (aby * aby);
  if (abLenSq <= 0.0000001) return Math.hypot(px - ax, py - ay);
  const t = clamp(((apx * abx) + (apy * aby)) / abLenSq, 0, 1);
  const cx = ax + (abx * t);
  const cy = ay + (aby * t);
  return Math.hypot(px - cx, py - cy);
};

const findNearestStrokeAtPoint = (strokes, point) => {
  if (!Array.isArray(strokes) || !strokes.length || !Array.isArray(point)) return null;
  const [px, py] = point;
  let best = null;
  strokes.forEach((stroke) => {
    const pts = Array.isArray(stroke?.points_norm) ? stroke.points_norm : [];
    if (pts.length < 2) return;
    const thickness = Math.max(2, Number(stroke?.thickness || 8));
    const threshold = Math.max(0.012, Math.min(0.045, (thickness / 900) * 2.4));
    let strokeBest = Infinity;
    for (let i = 1; i < pts.length; i += 1) {
      const a = pts[i - 1];
      const b = pts[i];
      const dist = distancePointToSegment(
        px,
        py,
        Number(a?.[0] || 0),
        Number(a?.[1] || 0),
        Number(b?.[0] || 0),
        Number(b?.[1] || 0)
      );
      if (dist < strokeBest) strokeBest = dist;
    }
    if (strokeBest <= threshold && (!best || strokeBest < best.distance)) {
      best = { id: String(stroke.id || ""), distance: strokeBest, stroke };
    }
  });
  return best;
};

const splitStrokeByMask = ({
  points = [],
  tool = "line",
  maskCanvas = null,
  width = 0,
  height = 0,
}) => {
  if (!Array.isArray(points) || points.length < 2) return [];
  if (!maskCanvas || width <= 1 || height <= 1) {
    return [{ tool, points_norm: points }];
  }
  const maskCtx = maskCanvas.getContext("2d");
  if (!maskCtx) return [{ tool, points_norm: points }];
  const data = maskCtx.getImageData(0, 0, width, height).data;
  const isInside = (nx, ny) => {
    const x = clamp(Math.round(nx * (width - 1)), 0, width - 1);
    const y = clamp(Math.round(ny * (height - 1)), 0, height - 1);
    const alpha = data[((y * width) + x) * 4 + 3];
    return alpha > 18;
  };
  const sampled = [];
  for (let i = 1; i < points.length; i += 1) {
    const a = points[i - 1];
    const b = points[i];
    const steps = Math.max(10, Math.round(Math.hypot(
      (Number(b?.[0] || 0) - Number(a?.[0] || 0)) * width,
      (Number(b?.[1] || 0) - Number(a?.[1] || 0)) * height
    ) / 6));
    for (let s = 0; s <= steps; s += 1) {
      const t = s / steps;
      const nx = Number(a?.[0] || 0) + ((Number(b?.[0] || 0) - Number(a?.[0] || 0)) * t);
      const ny = Number(a?.[1] || 0) + ((Number(b?.[1] || 0) - Number(a?.[1] || 0)) * t);
      sampled.push([clamp(nx, 0, 1), clamp(ny, 0, 1), isInside(nx, ny)]);
    }
  }
  const segments = [];
  let current = [];
  sampled.forEach((entry) => {
    const inside = !!entry[2];
    if (inside) {
      current.push([entry[0], entry[1]]);
      return;
    }
    if (current.length >= 2) {
      segments.push(current);
    }
    current = [];
  });
  if (current.length >= 2) segments.push(current);
  if (!segments.length) return [];
  return segments.map((segment) => ({
    tool: tool === "line" ? "line" : "curve",
    points_norm: tool === "line"
      ? [segment[0], segment[segment.length - 1]]
      : segment,
  }));
};

const STRIPE_ENDPOINT_SNAP_MIN_RADIUS_PX = 14;
const STRIPE_ENDPOINT_SNAP_MAX_RADIUS_PX = 36;
const STRIPE_ENDPOINT_SNAP_MIN_COS = -0.05;
const STRIPE_ENDPOINT_SNAP_CLOSE_DIST_PX = 14;
const STRIPE_ENDPOINT_SNAP_DIRECTION_ZONE = 0.78;
const STRIPE_ENDPOINT_SNAP_DILATION_PX = 2;
const STRIPE_ENDPOINT_SNAP_RETREAT_MIN_PX = 1.6;
const STRIPE_ENDPOINT_SNAP_RETREAT_MAX_PX = 3.2;
const STRIPE_SNAP_DEBUG_VISUAL = false;

const resolveStripeSnapRadiusPx = (thickness = 8) => clamp(
  (Number(thickness || 8) * 1.6) + 12,
  STRIPE_ENDPOINT_SNAP_MIN_RADIUS_PX,
  STRIPE_ENDPOINT_SNAP_MAX_RADIUS_PX
);

const buildSilhouetteEdgePoints = ({ maskAlpha, width, height }) => {
  if (!maskAlpha?.length || width <= 1 || height <= 1) return [];
  const points = [];
  const isInside = (x, y) => {
    if (x < 0 || y < 0 || x >= width || y >= height) return false;
    return maskAlpha[(y * width) + x] > 18;
  };
  for (let y = 1; y < height - 1; y += 1) {
    for (let x = 1; x < width - 1; x += 1) {
      if (!isInside(x, y)) continue;
      if (!isInside(x - 1, y) || !isInside(x + 1, y) || !isInside(x, y - 1) || !isInside(x, y + 1)) {
        points.push([x, y]);
      }
    }
  }
  return points;
};

const buildInternalContourPoints = ({ detailData, maskAlpha, width, height }) => {
  if (!detailData?.length || !maskAlpha?.length || width <= 2 || height <= 2) return [];
  const points = [];
  const getLum = (x, y) => {
    const idx = ((y * width) + x) * 4;
    return (0.2126 * detailData[idx]) + (0.7152 * detailData[idx + 1]) + (0.0722 * detailData[idx + 2]);
  };
  for (let y = 1; y < height - 1; y += 1) {
    for (let x = 1; x < width - 1; x += 1) {
      const p = (y * width) + x;
      if (maskAlpha[p] <= 18) continue;
      const idx = p * 4;
      const alpha = detailData[idx + 3];
      if (alpha < 32) continue;
      const lum = getLum(x, y);
      const grad = Math.max(
        Math.abs(lum - getLum(x - 1, y)),
        Math.abs(lum - getLum(x + 1, y)),
        Math.abs(lum - getLum(x, y - 1)),
        Math.abs(lum - getLum(x, y + 1))
      );
      const isContourLike = (lum < 132 && grad > 13) || grad > 42;
      if (isContourLike) points.push([x, y]);
    }
  }
  return points;
};

const buildLayerAlphaEdgePoints = ({ layerData, maskAlpha, width, height }) => {
  if (!layerData?.length || !maskAlpha?.length || width <= 2 || height <= 2) return [];
  const points = [];
  const alphaAt = (x, y) => layerData[((y * width) + x) * 4 + 3];
  for (let y = 1; y < height - 1; y += 1) {
    for (let x = 1; x < width - 1; x += 1) {
      const p = (y * width) + x;
      if (maskAlpha[p] <= 18) continue;
      const center = alphaAt(x, y);
      if (center <= 20) continue;
      const isEdge = alphaAt(x - 1, y) <= 20
        || alphaAt(x + 1, y) <= 20
        || alphaAt(x, y - 1) <= 20
        || alphaAt(x, y + 1) <= 20;
      if (isEdge) points.push([x, y]);
    }
  }
  return points;
};

const buildSnapTargetAlpha = (points = [], width = 0, height = 0) => {
  if (!Array.isArray(points) || !points.length || width <= 1 || height <= 1) {
    return new Uint8ClampedArray(0);
  }
  const alpha = new Uint8ClampedArray(width * height);
  points.forEach((point) => {
    const x = clamp(Math.round(Number(point?.[0] || 0)), 0, width - 1);
    const y = clamp(Math.round(Number(point?.[1] || 0)), 0, height - 1);
    alpha[(y * width) + x] = 255;
    for (let oy = -STRIPE_ENDPOINT_SNAP_DILATION_PX; oy <= STRIPE_ENDPOINT_SNAP_DILATION_PX; oy += 1) {
      for (let ox = -STRIPE_ENDPOINT_SNAP_DILATION_PX; ox <= STRIPE_ENDPOINT_SNAP_DILATION_PX; ox += 1) {
        const xx = x + ox;
        const yy = y + oy;
        if (xx < 0 || yy < 0 || xx >= width || yy >= height) continue;
        alpha[(yy * width) + xx] = 255;
      }
    }
  });
  return alpha;
};

const findNearestSnapPoint = ({
  endpoint = [],
  neighbor = [],
  snapTargetMap = null,
  thickness = 8,
}) => {
  const width = Number(snapTargetMap?.width || 0);
  const height = Number(snapTargetMap?.height || 0);
  const alpha = snapTargetMap?.alpha;
  if (width <= 1 || height <= 1 || !alpha?.length) return null;
  const ex = clamp(Number(endpoint?.[0] || 0), 0, 1) * width;
  const ey = clamp(Number(endpoint?.[1] || 0), 0, 1) * height;
  const nx = clamp(Number(neighbor?.[0] || 0), 0, 1) * width;
  const ny = clamp(Number(neighbor?.[1] || 0), 0, 1) * height;
  const vx = ex - nx;
  const vy = ey - ny;
  const vLen = Math.hypot(vx, vy);
  const dirX = vLen > 0.0001 ? vx / vLen : 0;
  const dirY = vLen > 0.0001 ? vy / vLen : 0;
  const radius = resolveStripeSnapRadiusPx(thickness);
  const maxDist = radius;
  const directionCheckDist = Math.max(
    STRIPE_ENDPOINT_SNAP_CLOSE_DIST_PX,
    radius * STRIPE_ENDPOINT_SNAP_DIRECTION_ZONE
  );

  let best = null;
  let nearest = null;
  const minX = clamp(Math.floor(ex - maxDist), 0, width - 1);
  const maxX = clamp(Math.ceil(ex + maxDist), 0, width - 1);
  const minY = clamp(Math.floor(ey - maxDist), 0, height - 1);
  const maxY = clamp(Math.ceil(ey + maxDist), 0, height - 1);
  for (let y = minY; y <= maxY; y += 1) {
    for (let x = minX; x <= maxX; x += 1) {
      if (alpha[(y * width) + x] <= 0) continue;
      const dx = x - ex;
      const dy = y - ey;
      const dist = Math.hypot(dx, dy);
      if (dist < 0.0001 || dist > maxDist) continue;
      if (!nearest || dist < nearest.distance) {
        nearest = {
          distance: dist,
          pointNorm: [clamp(x / width, 0, 1), clamp(y / height, 0, 1)],
        };
      }
      if (vLen > 0.0001 && dist > directionCheckDist) {
        const inv = 1 / dist;
        const cos = (dirX * dx * inv) + (dirY * dy * inv);
        if (cos < STRIPE_ENDPOINT_SNAP_MIN_COS) continue;
      }
      if (!best || dist < best.distance) {
        best = {
          distance: dist,
          pointNorm: [clamp(x / width, 0, 1), clamp(y / height, 0, 1)],
        };
      }
    }
  }

  if (!best && !nearest) return null;
  return {
    ...(best || {}),
    nearestPointNorm: nearest?.pointNorm || null,
    nearestDistance: Number(nearest?.distance || 0),
  };
};

const retreatSnapPointInside = ({
  snappedPoint = [],
  neighborPoint = [],
  thickness = 8,
  snapTargetMap = null,
}) => {
  const width = Number(snapTargetMap?.width || 0);
  const height = Number(snapTargetMap?.height || 0);
  if (width <= 1 || height <= 1) return snappedPoint;
  const sx = clamp(Number(snappedPoint?.[0] || 0), 0, 1) * width;
  const sy = clamp(Number(snappedPoint?.[1] || 0), 0, 1) * height;
  const nx = clamp(Number(neighborPoint?.[0] || 0), 0, 1) * width;
  const ny = clamp(Number(neighborPoint?.[1] || 0), 0, 1) * height;
  const dx = sx - nx;
  const dy = sy - ny;
  const length = Math.hypot(dx, dy);
  if (length <= 0.001) return snappedPoint;
  const retreatBase = clamp(
    (Number(thickness || 8) * 0.22),
    STRIPE_ENDPOINT_SNAP_RETREAT_MIN_PX,
    STRIPE_ENDPOINT_SNAP_RETREAT_MAX_PX
  );
  const retreatPx = Math.min(retreatBase, length * 0.6);
  const ux = dx / length;
  const uy = dy / length;
  const rx = sx - (ux * retreatPx);
  const ry = sy - (uy * retreatPx);
  return [clamp(rx / width, 0, 1), clamp(ry / height, 0, 1)];
};

const autoConnectStrokeEndpoints = ({
  points = [],
  tool = "line",
  thickness = 8,
  snapTargetMap = null,
}) => {
  const original = Array.isArray(points) ? points.map((p) => [clamp(Number(p?.[0] || 0), 0, 1), clamp(Number(p?.[1] || 0), 0, 1)]) : [];
  if (original.length < 2) {
    return { points: original, snapAppliedStart: false, snapAppliedEnd: false };
  }
  const next = original.map((p) => [...p]);
  const startNeighbor = next[1] || next[0];
  const endNeighbor = next[next.length - 2] || next[next.length - 1];
  const startSnap = findNearestSnapPoint({
    endpoint: next[0],
    neighbor: startNeighbor,
    snapTargetMap,
    thickness,
  });
  const endSnap = findNearestSnapPoint({
    endpoint: next[next.length - 1],
    neighbor: endNeighbor,
    snapTargetMap,
    thickness,
  });
  const snapAppliedStart = !!startSnap?.pointNorm;
  const snapAppliedEnd = !!endSnap?.pointNorm;
  if (snapAppliedStart) {
    next[0] = retreatSnapPointInside({
      snappedPoint: startSnap.pointNorm,
      neighborPoint: startNeighbor,
      thickness,
      snapTargetMap,
    });
  }
  if (snapAppliedEnd) {
    next[next.length - 1] = retreatSnapPointInside({
      snappedPoint: endSnap.pointNorm,
      neighborPoint: endNeighbor,
      thickness,
      snapTargetMap,
    });
  }
  if (String(tool || "").toLowerCase() === "line") {
    return {
      points: [next[0], next[next.length - 1]],
      snapAppliedStart,
      snapAppliedEnd,
      startTargetNorm: startSnap?.nearestPointNorm || null,
      endTargetNorm: endSnap?.nearestPointNorm || null,
    };
  }
  return {
    points: next,
    snapAppliedStart,
    snapAppliedEnd,
    startTargetNorm: startSnap?.nearestPointNorm || null,
    endTargetNorm: endSnap?.nearestPointNorm || null,
  };
};

const drawStripeSnapDebug = (ctx, stroke, widthPx, heightPx) => {
  if (!ctx || !stroke) return;
  const points = Array.isArray(stroke?.points_norm) ? stroke.points_norm : [];
  if (points.length < 2) return;
  const radius = resolveStripeSnapRadiusPx(Number(stroke?.thickness || 8));
  const drawCircle = (point, color, fillColor = "rgba(255,255,255,0.08)") => {
    const x = clamp(Number(point?.[0] || 0), 0, 1) * widthPx;
    const y = clamp(Number(point?.[1] || 0), 0, 1) * heightPx;
    ctx.save();
    ctx.beginPath();
    ctx.fillStyle = fillColor;
    ctx.strokeStyle = color;
    ctx.lineWidth = 1.5;
    ctx.arc(x, y, radius, 0, Math.PI * 2);
    ctx.fill();
    ctx.stroke();
    ctx.beginPath();
    ctx.fillStyle = color;
    ctx.arc(x, y, 2.2, 0, Math.PI * 2);
    ctx.fill();
    ctx.restore();
  };
  const drawTarget = (point, color = "rgba(168,85,247,0.95)") => {
    if (!Array.isArray(point) || point.length < 2) return;
    const x = clamp(Number(point?.[0] || 0), 0, 1) * widthPx;
    const y = clamp(Number(point?.[1] || 0), 0, 1) * heightPx;
    ctx.save();
    ctx.beginPath();
    ctx.setLineDash([5, 4]);
    ctx.strokeStyle = color;
    ctx.lineWidth = 1.2;
    ctx.arc(x, y, radius, 0, Math.PI * 2);
    ctx.stroke();
    ctx.setLineDash([]);
    ctx.beginPath();
    ctx.fillStyle = color;
    ctx.arc(x, y, 2.4, 0, Math.PI * 2);
    ctx.fill();
    ctx.restore();
  };
  const startColor = stroke?.snap_applied_start ? "rgba(16,185,129,0.95)" : "rgba(234,179,8,0.95)";
  const endColor = stroke?.snap_applied_end ? "rgba(16,185,129,0.95)" : "rgba(59,130,246,0.95)";
  drawCircle(points[0], startColor);
  drawCircle(points[points.length - 1], endColor);
  drawTarget(stroke?.snap_target_start_norm || null, "rgba(244,114,182,0.95)");
  drawTarget(stroke?.snap_target_end_norm || null, "rgba(168,85,247,0.95)");
};

const injectedFonts = new Set();
// Acepta una URL completa (file_url del ABM, /uploads/typographies) o un file relativo que se
// resuelve contra baseUrl (/fonts/master, tabla fija). Detecta otf/ttf por extensión.
const ensureFontFace = (family, fileOrUrl, baseUrl = MASTER_PRINT_FONT_BASE_URL) => {
  if (!family || !fileOrUrl || typeof document === "undefined") return;
  const id = `font-${family}`;
  if (injectedFonts.has(family) || document.getElementById(id)) return;
  const src = String(fileOrUrl).startsWith("http") ? String(fileOrUrl) : `${baseUrl}${fileOrUrl}`;
  const format = /\.otf(\?|$)/i.test(src) ? "opentype" : "truetype";
  const style = document.createElement("style");
  style.id = id;
  style.innerHTML = `
    @font-face {
      font-family: '${family}';
      src: url('${src}') format('${format}');
      font-weight: normal;
      font-style: normal;
    }
  `;
  document.head.appendChild(style);
  injectedFonts.add(family);
};

const PART_HIGHLIGHT_FILL = "rgba(230, 88, 197, 0.16)";
const PART_HIGHLIGHT_OUTLINE = "rgba(230, 88, 197, 0.34)";

const PreviewLayer = ({
  img,
  colorHex,
  patternUrl,
  zIndex,
  item,
  forceSolidFill = false,
  highlightOverlay = false,
  sparkleToken = 0,
  sparkleActive = false,
  customCutZoneOverlays = [],
}) => {
  const url = getImgUrl(img);
  const pattern = patternUrl ? getImgUrl(patternUrl) : null;
  const canvasRef = useRef(null);
  const highlightCanvasRef = useRef(null);
  const sparkleCanvasRef = useRef(null);
  const [baseLoadFailed, setBaseLoadFailed] = useState(false);
  const [sparkleVisible, setSparkleVisible] = useState(false);

  useEffect(() => {
    setBaseLoadFailed(false);
  }, [url]);

  useEffect(() => {
    const canvas = canvasRef.current;
    const zoneOverlays = Array.isArray(customCutZoneOverlays) ? customCutZoneOverlays : [];
    if (!canvas || baseLoadFailed || (!pattern && !colorHex && !zoneOverlays.length)) return;
    const ctx = canvas.getContext("2d");
    const load = (src) => new Promise((resolve, reject) => {
      const im = new Image();
      im.crossOrigin = "anonymous";
      im.onload = () => resolve(im);
      im.onerror = reject;
      im.src = src;
    });
    let cancelled = false;
    (async () => {
      try {
        const maskImg = await load(url);
        if (cancelled) return;
        const pattImg = pattern ? await load(pattern) : null;
        if (cancelled) return;
        const w = maskImg.naturalWidth || pattImg?.naturalWidth || 900;
        const h = maskImg.naturalHeight || pattImg?.naturalHeight || 900;
        if (cancelled) return;
        canvas.width = w;
        canvas.height = h;
        ctx.clearRect(0, 0, w, h);
        if (pattImg) {
          if (cancelled) return;
          ctx.drawImage(pattImg, 0, 0, pattImg.naturalWidth, pattImg.naturalHeight, 0, 0, w, h);
        } else if (forceSolidFill && colorHex) {
          ctx.clearRect(0, 0, w, h);
          const probe = document.createElement("canvas");
          probe.width = w;
          probe.height = h;
          const probeCtx = probe.getContext("2d");
          if (!probeCtx) return;
          probeCtx.drawImage(maskImg, 0, 0, maskImg.naturalWidth, maskImg.naturalHeight, 0, 0, w, h);
          const data = probeCtx.getImageData(0, 0, w, h).data;
          let minX = w;
          let minY = h;
          let maxX = -1;
          let maxY = -1;
          for (let y = 0; y < h; y += 1) {
            for (let x = 0; x < w; x += 1) {
              const alpha = data[((y * w) + x) * 4 + 3];
              if (alpha > 8) {
                if (x < minX) minX = x;
                if (x > maxX) maxX = x;
                if (y < minY) minY = y;
                if (y > maxY) maxY = y;
              }
            }
          }
          if (maxX >= minX && maxY >= minY) {
            const padX = Math.max(1, Math.round((maxX - minX) * 0.04));
            const padY = Math.max(1, Math.round((maxY - minY) * 0.35));
            const x = clamp(minX - padX, 0, w);
            const y = clamp(minY - padY, 0, h);
            const fillW = clamp((maxX - minX) + (padX * 2), 1, w - x);
            const fillH = clamp((maxY - minY) + (padY * 2), 1, h - y);
            ctx.globalAlpha = 0.92;
            ctx.fillStyle = colorHex;
            ctx.fillRect(x, y, fillW, fillH);
            ctx.globalAlpha = 1;
          }
        } else if (colorHex) {
          if (cancelled) return;
          ctx.fillStyle = colorHex;
          ctx.fillRect(0, 0, w, h);
        }
        if (cancelled) return;
        zoneOverlays.forEach((zone) => {
          if (cancelled) return;
          if (!zone?.color?.code || !zone?.maskCanvas) return;
          const overlayCanvas = document.createElement("canvas");
          overlayCanvas.width = w;
          overlayCanvas.height = h;
          const overlayCtx = overlayCanvas.getContext("2d");
          if (!overlayCtx) return;
          overlayCtx.drawImage(zone.maskCanvas, 0, 0, w, h);
          overlayCtx.globalCompositeOperation = "source-in";
          overlayCtx.fillStyle = zone.color.code;
          overlayCtx.fillRect(0, 0, w, h);
          overlayCtx.globalCompositeOperation = "source-over";
          ctx.drawImage(overlayCanvas, 0, 0, w, h);
        });
        if (cancelled) return;
        ctx.globalCompositeOperation = "destination-in";
        ctx.drawImage(maskImg, 0, 0, maskImg.naturalWidth, maskImg.naturalHeight, 0, 0, w, h);
        ctx.globalCompositeOperation = "source-over";
      } catch (e) {
        if (!cancelled) {
          ctx.clearRect(0, 0, canvas.width, canvas.height);
        }
      }
    })();
    return () => { cancelled = true; };
  }, [url, pattern, colorHex, baseLoadFailed, forceSolidFill, customCutZoneOverlays]);

  useEffect(() => {
    const canvas = highlightCanvasRef.current;
    const ctx = canvas?.getContext("2d");
    if (!canvas || !ctx) return;
    if (baseLoadFailed || !highlightOverlay) {
      ctx.clearRect(0, 0, canvas.width || 0, canvas.height || 0);
      return;
    }
    const load = (src) => new Promise((resolve, reject) => {
      const im = new Image();
      im.crossOrigin = "anonymous";
      im.onload = () => resolve(im);
      im.onerror = reject;
      im.src = src;
    });
    let cancelled = false;
    (async () => {
      try {
        const maskImg = await load(url);
        if (cancelled) return;
        const w = maskImg.naturalWidth || 900;
        const h = maskImg.naturalHeight || 900;
        canvas.width = w;
        canvas.height = h;
        ctx.clearRect(0, 0, w, h);

        const fillCanvas = document.createElement("canvas");
        fillCanvas.width = w;
        fillCanvas.height = h;
        const fillCtx = fillCanvas.getContext("2d");
        if (!fillCtx) return;
        fillCtx.drawImage(maskImg, 0, 0, maskImg.naturalWidth, maskImg.naturalHeight, 0, 0, w, h);
        fillCtx.globalCompositeOperation = "source-in";
        fillCtx.fillStyle = PART_HIGHLIGHT_FILL;
        fillCtx.fillRect(0, 0, w, h);
        fillCtx.globalCompositeOperation = "source-over";

        const outlineCanvas = document.createElement("canvas");
        outlineCanvas.width = w;
        outlineCanvas.height = h;
        const outlineCtx = outlineCanvas.getContext("2d");
        if (!outlineCtx) return;
        outlineCtx.fillStyle = PART_HIGHLIGHT_OUTLINE;
        const offsets = [
          [-2, 0], [2, 0], [0, -2], [0, 2],
          [-2, -2], [-2, 2], [2, -2], [2, 2],
        ];
        offsets.forEach(([ox, oy]) => {
          outlineCtx.drawImage(maskImg, ox, oy, w, h);
        });
        outlineCtx.globalCompositeOperation = "destination-out";
        outlineCtx.drawImage(maskImg, 0, 0, maskImg.naturalWidth, maskImg.naturalHeight, 0, 0, w, h);
        outlineCtx.globalCompositeOperation = "source-over";

        ctx.drawImage(outlineCanvas, 0, 0);
        ctx.drawImage(fillCanvas, 0, 0);
      } catch (_error) {
        if (!cancelled) {
          ctx.clearRect(0, 0, canvas.width || 0, canvas.height || 0);
        }
      }
    })();
    return () => { cancelled = true; };
  }, [url, baseLoadFailed, highlightOverlay]);

  useEffect(() => {
    if (!sparkleActive || !sparkleToken) {
      setSparkleVisible(false);
      return undefined;
    }
    setSparkleVisible(true);
    const timeoutId = window.setTimeout(() => {
      setSparkleVisible(false);
    }, 650);
    return () => window.clearTimeout(timeoutId);
  }, [sparkleActive, sparkleToken]);

  useEffect(() => {
    const canvas = sparkleCanvasRef.current;
    const ctx = canvas?.getContext("2d");
    if (!canvas || !ctx) return;
    if (baseLoadFailed || !sparkleVisible) {
      ctx.clearRect(0, 0, canvas.width || 0, canvas.height || 0);
      return;
    }
    const load = (src) => new Promise((resolve, reject) => {
      const im = new Image();
      im.crossOrigin = "anonymous";
      im.onload = () => resolve(im);
      im.onerror = reject;
      im.src = src;
    });
    let cancelled = false;
    (async () => {
      try {
        const maskImg = await load(url);
        if (cancelled) return;
        const w = maskImg.naturalWidth || 900;
        const h = maskImg.naturalHeight || 900;
        canvas.width = w;
        canvas.height = h;
        ctx.clearRect(0, 0, w, h);

        const probe = document.createElement("canvas");
        probe.width = w;
        probe.height = h;
        const probeCtx = probe.getContext("2d");
        if (!probeCtx) return;
        probeCtx.drawImage(maskImg, 0, 0, maskImg.naturalWidth, maskImg.naturalHeight, 0, 0, w, h);
        const data = probeCtx.getImageData(0, 0, w, h).data;

        const validPoints = [];
        for (let y = 4; y < h - 4; y += 6) {
          for (let x = 4; x < w - 4; x += 6) {
            const alpha = data[((y * w) + x) * 4 + 3];
            if (alpha > 18) validPoints.push([x, y]);
          }
        }
        if (!validPoints.length) return;

        const sparkleCount = Math.min(14, Math.max(8, Math.round(validPoints.length / 140)));
        const chosen = [];
        for (let i = 0; i < sparkleCount; i += 1) {
          const index = Math.floor(Math.random() * validPoints.length);
          chosen.push(validPoints[index]);
        }

        ctx.save();
        chosen.forEach(([x, y], index) => {
          const size = 6 + ((index % 3) * 2);
          ctx.globalAlpha = 1;
          ctx.strokeStyle = "#e658c5";
          ctx.lineWidth = 1.6;
          ctx.beginPath();
          ctx.moveTo(x - size, y);
          ctx.lineTo(x + size, y);
          ctx.moveTo(x, y - size);
          ctx.lineTo(x, y + size);
          ctx.stroke();

          ctx.globalAlpha = 1;
          ctx.strokeStyle = "#ffffff";
          ctx.lineWidth = 1;
          ctx.beginPath();
          ctx.moveTo(x - (size * 0.55), y - (size * 0.55));
          ctx.lineTo(x + (size * 0.55), y + (size * 0.55));
          ctx.moveTo(x + (size * 0.55), y - (size * 0.55));
          ctx.lineTo(x - (size * 0.55), y + (size * 0.55));
          ctx.stroke();
        });
        ctx.restore();
      } catch (_error) {
        if (!cancelled) {
          ctx.clearRect(0, 0, canvas.width || 0, canvas.height || 0);
        }
      }
    })();
    return () => { cancelled = true; };
  }, [url, baseLoadFailed, sparkleVisible, sparkleToken]);

  if (!img) return null;
  if (baseLoadFailed) return null;

  // 🪤 Registro de capas de parte: el stage es CUADRADO (aspect-ratio 1/1) y las imágenes de
  // parte son canónicamente 900×900. Con `object-fit: contain`, cada capa (color img_front_t vs
  // líneas img_front) se ajustaba según SU propio tamaño → si una parte traía las dos imágenes
  // con tamaños/proporción distintos, el relleno quedaba CORRIDO respecto del contorno (se veía
  // sobre todo desbordando la cintura). Con `fill` cada capa ocupa exactamente la caja cuadrada
  // del stage → todas registran igual, venga el dato parejo o no. Para 900² no cambia nada.
  // ⚠️ REQUIERE que las capas (img_*) sean CUADRADAS. Hoy TODAS lo son (medidos 2.172 PNG de
  // master-parts en prod: 0 img_* no cuadradas; las no-cuadradas son solo icon_*, que no pasan
  // por el stage). Si alguien sube una máscara NO cuadrada desde el ABM de partes, `fill` la
  // ESTIRA en silencio (contain la mostraría mal pero visible). Guard anti-no-cuadrada en el
  // ABM del lado V4; si se saca, volver a mirar esto.
  const LAYER_FIT = "fill";
  return (
    <div style={{ ...posStyle(item), zIndex }}>
      <img
        src={url}
        alt="layer"
        onError={() => setBaseLoadFailed(true)}
        style={{ width: "100%", height: "100%", objectFit: LAYER_FIT }}
      />
      {(colorHex || pattern) && (
        <canvas
          ref={canvasRef}
          style={{
            position: "absolute",
            inset: 0,
            width: "100%",
            height: "100%",
            objectFit: LAYER_FIT,
            pointerEvents: "none",
          }}
        />
      )}
      {highlightOverlay ? (
        <canvas
          ref={highlightCanvasRef}
          style={{
            position: "absolute",
            inset: 0,
            width: "100%",
            height: "100%",
            objectFit: LAYER_FIT,
            pointerEvents: "none",
          }}
        />
      ) : null}
      {sparkleVisible ? (
        <canvas
          ref={sparkleCanvasRef}
          className={styles.partSparkleOverlay}
          style={{
            position: "absolute",
            inset: 0,
            width: "100%",
            height: "100%",
            objectFit: LAYER_FIT,
            pointerEvents: "none",
          }}
        />
      ) : null}
    </div>
  );
};

const canvasImageCache = new Map();
const canvasImageOpaqueBoundsCache = new Map();
const canvasMaskHitCache = new Map();

const loadCanvasImage = (src) => {
  if (!src) return Promise.reject(new Error("empty src"));
  if (canvasImageCache.has(src)) return canvasImageCache.get(src);
  const loadPromise = new Promise((resolve, reject) => {
    const image = new Image();
    image.crossOrigin = "anonymous";
    image.onload = () => resolve(image);
    image.onerror = () => {
      canvasImageCache.delete(src);
      reject(new Error("load error"));
    };
    image.src = src;
  });
  canvasImageCache.set(src, loadPromise);
  return loadPromise;
};

const getCanvasImageOpaqueBounds = (src) => {
  if (!src) return Promise.resolve(null);
  if (canvasImageOpaqueBoundsCache.has(src)) return canvasImageOpaqueBoundsCache.get(src);
  const boundsPromise = loadCanvasImage(src)
    .then((image) => {
      const width = Math.max(1, Number(image.naturalWidth || image.width || 1));
      const height = Math.max(1, Number(image.naturalHeight || image.height || 1));
      const probe = document.createElement("canvas");
      probe.width = width;
      probe.height = height;
      const probeCtx = probe.getContext("2d", { willReadFrequently: true });
      if (!probeCtx) {
        return { x: 0, y: 0, width, height, aspectRatio: width / height };
      }
      probeCtx.clearRect(0, 0, width, height);
      probeCtx.drawImage(image, 0, 0, width, height);
      const imageData = probeCtx.getImageData(0, 0, width, height);
      let minX = width;
      let minY = height;
      let maxX = -1;
      let maxY = -1;
      for (let y = 0; y < height; y += 1) {
        for (let x = 0; x < width; x += 1) {
          const alpha = imageData.data[((y * width) + x) * 4 + 3];
          if (alpha <= 8) continue;
          if (x < minX) minX = x;
          if (y < minY) minY = y;
          if (x > maxX) maxX = x;
          if (y > maxY) maxY = y;
        }
      }
      if (maxX < minX || maxY < minY) {
        return { x: 0, y: 0, width, height, aspectRatio: width / height };
      }
      const boundsWidth = Math.max(1, (maxX - minX) + 1);
      const boundsHeight = Math.max(1, (maxY - minY) + 1);
      return {
        x: minX,
        y: minY,
        width: boundsWidth,
        height: boundsHeight,
        aspectRatio: boundsWidth / boundsHeight,
      };
    })
    .catch(() => null);
  canvasImageOpaqueBoundsCache.set(src, boundsPromise);
  return boundsPromise;
};

const getCanvasMaskHitMap = async (src, width, height) => {
  if (!src || !width || !height) return null;
  const cacheKey = `${src}::${width}x${height}`;
  if (canvasMaskHitCache.has(cacheKey)) return canvasMaskHitCache.get(cacheKey);
  const hitPromise = loadCanvasImage(src)
    .then((image) => {
      const probe = document.createElement("canvas");
      probe.width = Math.max(1, width);
      probe.height = Math.max(1, height);
      const probeCtx = probe.getContext("2d", { willReadFrequently: true });
      if (!probeCtx) return null;
      probeCtx.clearRect(0, 0, probe.width, probe.height);
      drawContainImage(probeCtx, image, probe.width, probe.height);
      const data = probeCtx.getImageData(0, 0, probe.width, probe.height).data;
      return {
        width: probe.width,
        height: probe.height,
        data,
      };
    })
    .catch(() => null);
  canvasMaskHitCache.set(cacheKey, hitPromise);
  return hitPromise;
};

const buildGarmentMask = ({ width, height, garmentContour = null, garmentBounds = null }) => {
  const bound = (val, min, max) => Math.max(min, Math.min(max, val));
  const safeWidth = Math.max(1, Math.round(Number(width || 0)));
  const safeHeight = Math.max(1, Math.round(Number(height || 0)));
  const maskCanvas = document.createElement("canvas");
  maskCanvas.width = safeWidth;
  maskCanvas.height = safeHeight;
  const maskCtx = maskCanvas.getContext("2d");
  if (!maskCtx) return maskCanvas;
  maskCtx.clearRect(0, 0, safeWidth, safeHeight);
  maskCtx.fillStyle = "#fff";

  if (garmentContour?.rowBounds && garmentContour.rowBounds.length > 0) {
    garmentContour.rowBounds.forEach((row, y) => {
      if (!row) return;
      const min = bound(Math.round(Number(row.min || 0)), 0, safeWidth - 1);
      const max = bound(Math.round(Number(row.max || 0)), 0, safeWidth - 1);
      if (max < min) return;
      if (y < 0 || y >= safeHeight) return;
      maskCtx.fillRect(min, y, (max - min) + 1, 1);
    });
    return maskCanvas;
  }

  if (garmentBounds) {
    const left = bound(Math.round((Number(garmentBounds.left || 0) / 100) * safeWidth), 0, safeWidth - 1);
    const right = bound(Math.round((Number(garmentBounds.right || 100) / 100) * safeWidth), left + 1, safeWidth);
    const top = bound(Math.round((Number(garmentBounds.top || 0) / 100) * safeHeight), 0, safeHeight - 1);
    const bottom = bound(Math.round((Number(garmentBounds.bottom || 100) / 100) * safeHeight), top + 1, safeHeight);
    maskCtx.fillRect(left, top, Math.max(1, right - left), Math.max(1, bottom - top));
    return maskCanvas;
  }

  maskCtx.fillRect(0, 0, safeWidth, safeHeight);
  return maskCanvas;
};


const clamp = (val, min, max) => Math.max(min, Math.min(max, val));

const PrintOverlay = ({
  node,
  printKey,
  selection,
  boostZ,
  onTransform,
  active = false,
  onSelect,
  stageMetrics,
  garmentBounds,
  garmentContour,
  garmentMaskCanvas,
  stripeCollisionMap = null,
  onStripeCollision = null,
  onClipByMask = null,
  interactive = true,
  allColors = [],
  onDeletePrint = null,
  onEditColor = null,
}) => {
  const item = node?.Item || {};
  const fallback = node ? (LEGACY_PRINT_MAP[node.key] || {}) : {};
  // Un prediseño arranca "fijo" (tamaño por tier), PERO si el cliente lo agrandó a mano
  // (free_scale=1) pasa a escalado libre como el texto.
  const isFixedPredesign = isFixedPredesignSelection(selection)
    && String(selection?.attrs?.free_scale || "") !== "1";
  const isImageUpload = !!String(selection?.attrs?.image_url || "").trim();

  const topVal = item.top != null ? item.top : fallback.top;
  const leftVal = item.left != null ? item.left : fallback.left;
  const baseTop = coordToPct(topVal, stageMetrics?.height || 0, stageMetrics?.baseSize || LEGACY_BASE_SIZE) ?? 0;
  const baseLeft = coordToPct(leftVal, stageMetrics?.width || 0, stageMetrics?.baseSize || LEGACY_BASE_SIZE) ?? 0;
  const isCustomPrint = String(selection?.print?.id || "").startsWith("custom-");
  const useLegacyCenteredAnchor = isCustomPrint && String(selection?.attrs?.offset_origin || "").toLowerCase() !== "hotspot";
  // Escudo de POSICIÓN FIJA: centro absoluto (abs_left/top_pct) + alto fijo (cm) + bloqueado
  // (sin mover ni redimensionar). Lo setea el escudo en el ABM (fixed_placement).
  const fixedAbsLeftPct = Number(selection?.attrs?.abs_left_pct);
  const fixedAbsTopPct = Number(selection?.attrs?.abs_top_pct);
  const hasFixedPlacement = Number.isFinite(fixedAbsLeftPct) && Number.isFinite(fixedAbsTopPct);
  const fixedHeightCm = Number(selection?.attrs?.fixed_height_cm) || Number(selection?.attrs?.real_height_cm);
  const hasFixedHeight = hasFixedPlacement && Number.isFinite(fixedHeightCm) && fixedHeightCm > 0;
  const anchorTop = hasFixedPlacement ? fixedAbsTopPct : (useLegacyCenteredAnchor ? Number(garmentBounds?.centerTop ?? 50) : baseTop);
  const anchorLeft = hasFixedPlacement ? fixedAbsLeftPct : (useLegacyCenteredAnchor ? Number(garmentBounds?.centerLeft ?? 50) : baseLeft);
  const isText = selection?.mode === "text" || (!!selection?.attrs?.text && !selection?.attrs?.image_url);
  const customImageSrc = selection?.attrs?.image_url || null;
  const normalizedPrintImg =
    selection?.print?.img && String(selection.print.img).startsWith("http")
      ? String(selection.print.img)
      : (selection?.print?.img
        ? (String(selection.print.img).includes("/")
          ? String(selection.print.img) // ruta de storage V4 → la resuelve getImgUrl
          : `${CDN_BASE}/uploads/charly/prints/${selection.print.id}/${selection.print.img}`)
        : null);
  const normalizedPrintIcon =
    selection?.print?.icon_url ||
    (selection?.print?.icon
      ? (String(selection.print.icon).includes("/")
        ? String(selection.print.icon)
        : `${CDN_BASE}/uploads/charly/prints/${selection.print.id}/icon/${selection.print.icon}`)
      : null);
  const normalizedPrintFull =
    selection?.print?.img_full
    || selection?.print?.img_url
    || selection?.print?.image_url
    || null;
  const hasTintColor =
    !!selection?.attrs?.color_hex
    || (Array.isArray(selection?.attrs?.color_hexes) && selection.attrs.color_hexes.length > 0)
    || !!selection?.attrs?.color1
    || !!selection?.attrs?.color2
    || !!selection?.attrs?.color3
    || !!selection?.attrs?.color;
  const resolvedTechnique = String(selection?.attrs?.resolved_technique || "").toLowerCase();
  const isEmbroidery = resolvedTechnique === "bordado";
  // Texto con relleno + contorno (2 colores): bordado, aplique, o estampado con "2do color" (borde).
  const wantsTextOutline = isEmbroidery || String(selection?.attrs?.text_border || "") === "1";
  // El tintado NO depende del tamaño: un escudo pintable se puede seguir pintando después de
  // agrandarlo a mano (free_scale=1). Antes exigía isFixedPredesign → al agrandar perdía el color.
  const canTintPredesign = !isText
    && !customImageSrc
    && hasTintColor
    && isTintablePrint(selection?.print);
  // customImageSrc puede ser data:/blob: (imagen propia) → NO tocar. Las rutas del print (relativas
  // de V4 o absolutas) se resuelven con getImgUrl (rutas relativas de storage → V4, no v3core).
  const src = customImageSrc || getImgUrl(normalizedPrintFull || normalizedPrintImg || normalizedPrintIcon);
  const shouldHide = !isText && !src;
  const effectiveSelection = isFixedPredesign
    ? {
      ...selection,
      attrs: applyPredesignSizeAttrs(selection),
    }
    : selection;
  const fixedPredesignScale = Number(effectiveSelection?.attrs?.scale_pct || 100) || 100;
  const [imageAspectRatio, setImageAspectRatio] = useState(1);
  const [imageOpaqueBounds, setImageOpaqueBounds] = useState(null);

  const widthCm =
    selection?.attrs?.max_ancho ||
    selection?.attrs?.maxw ||
    selection?.print?.max_ancho ||
    selection?.print?.maxw ||
    fallback.maxw ||
    DEFAULT_PRINT_BASE_SIZE_CM;

  const [state, setState] = useState(() => ({
    dx: selection?.attrs?.offset_x_pct ? Number(selection.attrs.offset_x_pct) : 0,
    dy: selection?.attrs?.offset_y_pct ? Number(selection.attrs.offset_y_pct) : 0,
    scale: isFixedPredesign ? fixedPredesignScale : (selection?.attrs?.scale_pct ? Number(selection.attrs.scale_pct) : 100),
    rotation: Number(selection?.attrs?.rotation_deg || 0) || 0,
  }));
  const [toolsVisible, setToolsVisible] = useState(true);
  const latestStateRef = useRef(state);
  const lastNonCollidingStateRef = useRef(state);

  useEffect(() => {
    const fontId = selection?.attrs?.font;
    const fontDef = fontId ? getFontById(fontId) : null;
    const fontUrl = getFontFaceUrl(fontDef);
    if (fontDef?.family && fontUrl) {
      ensureFontFace(fontDef.family, fontUrl);
    }
  }, [selection?.attrs?.font]);

  useEffect(() => {
    let cancelled = false;
    if (isText || !src) {
      setImageAspectRatio(1);
      setImageOpaqueBounds(null);
      return undefined;
    }
    const loader = isFixedPredesign ? getCanvasImageOpaqueBounds(src) : loadCanvasImage(src).then((image) => {
      if (!image) return null;
      const sourceW = Math.max(1, Number(image.naturalWidth || image.width || 1));
      const sourceH = Math.max(1, Number(image.naturalHeight || image.height || 1));
      return { x: 0, y: 0, width: sourceW, height: sourceH, aspectRatio: sourceW / sourceH };
    });
    loader
      .then((bounds) => {
        if (!bounds || cancelled) return;
        const nextRatio = Math.max(0.05, Number(bounds.aspectRatio || 1));
        setImageAspectRatio(nextRatio);
        setImageOpaqueBounds(bounds);
      })
      .catch(() => {
        if (!cancelled) {
          setImageAspectRatio(1);
          setImageOpaqueBounds(null);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [isFixedPredesign, isText, src]);

  useEffect(() => {
    if (dragging.current) return;
    setState({
      dx: selection?.attrs?.offset_x_pct ? Number(selection.attrs.offset_x_pct) : 0,
      dy: selection?.attrs?.offset_y_pct ? Number(selection.attrs.offset_y_pct) : 0,
      scale: isFixedPredesign ? fixedPredesignScale : (selection?.attrs?.scale_pct ? Number(selection.attrs.scale_pct) : 100),
      rotation: Number(selection?.attrs?.rotation_deg || 0) || 0,
    });
  }, [fixedPredesignScale, isFixedPredesign, selection?.attrs?.offset_x_pct, selection?.attrs?.offset_y_pct, selection?.attrs?.scale_pct, selection?.attrs?.rotation_deg]);

  useEffect(() => {
    latestStateRef.current = state;
  }, [state]);

  useEffect(() => {
    if (dragging.current) return;
    const next = {
      dx: selection?.attrs?.offset_x_pct ? Number(selection.attrs.offset_x_pct) : 0,
      dy: selection?.attrs?.offset_y_pct ? Number(selection.attrs.offset_y_pct) : 0,
      scale: isFixedPredesign ? fixedPredesignScale : (selection?.attrs?.scale_pct ? Number(selection.attrs.scale_pct) : 100),
      rotation: Number(selection?.attrs?.rotation_deg || 0) || 0,
    };
    lastNonCollidingStateRef.current = next;
  }, [fixedPredesignScale, isFixedPredesign, selection?.attrs?.offset_x_pct, selection?.attrs?.offset_y_pct, selection?.attrs?.scale_pct, selection?.attrs?.rotation_deg]);

  const dragging = useRef(false);
  const dragPointerId = useRef(null);
  const lastPos = useRef({ x: 0, y: 0 });
  const dragTargetRef = useRef(null);
  const resizeRef = useRef({ active: false, pointerId: null, cx: 0, cy: 0, startDist: 1, startScale: 100 });

  const buildReactiveDimensionAttrs = useCallback((scaleOverride = null) => {
    const nextScale = isFixedPredesign ? fixedPredesignScale : (scaleOverride ?? state.scale);
    const nextSelection = {
      ...selection,
      attrs: {
        ...(selection?.attrs || {}),
        scale_pct: String(nextScale),
      },
    };
    const dims = getSelectionEffectiveDimensionsCm(nextSelection, {
      aspectRatioOverride: isText ? null : Math.max(0.05, Number(imageAspectRatio || 1)),
      scalePctOverride: nextScale,
    });
    const roundedWidth = roundUpToHalfCm(dims.width || 0).toFixed(1);
    const roundedHeight = roundUpToHalfCm(dims.height || 0).toFixed(1);
    return {
      width: roundedWidth,
      height: roundedHeight,
      rendered_width_cm: roundedWidth,
      rendered_height_cm: roundedHeight,
      rendered_aspect_ratio: Number(Math.max(0.05, Number(imageAspectRatio || 1))).toFixed(4),
    };
  }, [fixedPredesignScale, imageAspectRatio, isFixedPredesign, isText, selection, state.scale]);

  const commit = useCallback((next, atLimit = false, meta = null) => {
    if (typeof onTransform === "function") {
      const dimensionAttrs = buildReactiveDimensionAttrs(next.scale);
      onTransform(String(printKey || node.key), {
        offset_x_pct: next.dx.toFixed(2),
        offset_y_pct: next.dy.toFixed(2),
        scale_pct: (isFixedPredesign ? fixedPredesignScale : next.scale).toFixed(2),
        rotation_deg: next.rotation.toFixed(2),
        limit_hit: atLimit ? "1" : "0",
        ...dimensionAttrs,
      }, meta || undefined);
    }
  }, [buildReactiveDimensionAttrs, fixedPredesignScale, isFixedPredesign, node.key, printKey, onTransform]);

  useEffect(() => {
    if (typeof onTransform !== "function") return;
    if (!selection?.print) return;
    if (dragging.current) return;
    const dimensionAttrs = buildReactiveDimensionAttrs();
    const currentWidth = String(selection?.attrs?.rendered_width_cm || selection?.attrs?.width || "");
    const currentHeight = String(selection?.attrs?.rendered_height_cm || selection?.attrs?.height || "");
    const nextWidth = String(dimensionAttrs.width || "");
    const nextHeight = String(dimensionAttrs.height || "");
    const currentAspect = String(selection?.attrs?.rendered_aspect_ratio || "");
    const nextAspect = String(dimensionAttrs.rendered_aspect_ratio || "");
    if (
      currentWidth === nextWidth
      && currentHeight === nextHeight
      && currentAspect === nextAspect
    ) {
      return;
    }
    onTransform(String(printKey || node.key), dimensionAttrs, { phase: "sync-dimensions" });
  }, [
    buildReactiveDimensionAttrs,
    isText,
    node.key,
    onTransform,
    printKey,
    selection,
  ]);

  // El TAMAÑO del print es su ALTO real (cm) = base (widthCm) × escala, mapeado a % del ALTO del
  // stage (11 px/cm). targetBoxPct = alto del print. El ancho sale del aspecto (imagen) o se
  // aproxima (texto: la caja/handles; el ancho real del texto lo resuelve fillText).
  const baseWidthPct = Math.min(Math.max((widthCm / PRINT_SCALE_CM_H) * 100, 1), 95);
  // Alto fijo (cm) para el escudo de posición fija; si no, alto = base × escala como siempre.
  const targetBoxPct = hasFixedHeight
    ? Math.min(Math.max((fixedHeightCm / PRINT_SCALE_CM_H) * 100, 1), 95)
    : (baseWidthPct * state.scale) / 100;
  const resolvedImageAspectRatio = Math.max(0.05, Number(imageAspectRatio || 1));
  const effectiveHeightPct = targetBoxPct;
  const effectiveWidthPct = isText
    ? Math.min(targetBoxPct * 2.2, 95)
    : targetBoxPct * resolvedImageAspectRatio;
  const movementWidthPct = isText ? Math.max(2, effectiveWidthPct * 0.5) : effectiveWidthPct;
  const movementHeightPct = isText ? Math.max(2, effectiveHeightPct) : effectiveHeightPct;
  const constrainToGarment = useCallback((dx, dy, widthForConstraint = movementWidthPct, heightForConstraint = movementHeightPct) => {
    const stageWidth = Math.max(1, Number(stageMetrics?.width || 0));
    const stageHeight = Math.max(1, Number(stageMetrics?.height || 0));
    const halfWpx = Math.max(1, (widthForConstraint / 100) * stageWidth * 0.5);
    const halfHpx = Math.max(1, (heightForConstraint / 100) * stageHeight * 0.5);
    const constraintHalfWpx = Math.max(4, halfWpx - PRINT_CONSTRAINT_INSET_PX);
    const constraintHalfHpx = Math.max(4, halfHpx - PRINT_CONSTRAINT_INSET_PX);
    const anchorXpx = (anchorLeft / 100) * stageWidth;
    const anchorYpx = (anchorTop / 100) * stageHeight;
    let centerXpx = anchorXpx + ((Number(dx || 0) / 100) * stageWidth);
    let centerYpx = anchorYpx + ((Number(dy || 0) / 100) * stageHeight);
    const originalCenterXpx = centerXpx;
    const originalCenterYpx = centerYpx;

    if (garmentContour?.rowBounds && garmentContour.rowBounds.length > 0) {
      const topPx = Number(garmentContour.topPx ?? 0);
      const bottomPx = Number(garmentContour.bottomPx ?? stageHeight);
      const rowBounds = garmentContour.rowBounds;
      const clampRowIdx = (v) => clamp(Math.round(v), 0, rowBounds.length - 1);
      const getRangeForCenterY = (centerYValuePx) => {
        const samples = isText
          ? [clampRowIdx(centerYValuePx)]
          : [
            clampRowIdx(centerYValuePx - halfHpx),
            clampRowIdx(centerYValuePx),
            clampRowIdx(centerYValuePx + halfHpx),
          ];
        let minCenter = -Infinity;
        let maxCenter = Infinity;
          samples.forEach((rowIdx) => {
            const row = rowBounds[rowIdx];
            if (!row) return;
            minCenter = Math.max(minCenter, Number(row.min) + constraintHalfWpx);
            maxCenter = Math.min(maxCenter, Number(row.max) - constraintHalfWpx);
          });
        if (!Number.isFinite(minCenter) || !Number.isFinite(maxCenter) || minCenter > maxCenter) {
          return null;
        }
        return { minCenter, maxCenter };
      };

      const minCenterY = topPx + constraintHalfHpx;
      const maxCenterY = bottomPx - constraintHalfHpx;
      centerYpx = clamp(centerYpx, Math.min(minCenterY, maxCenterY), Math.max(minCenterY, maxCenterY));

      let xRange = getRangeForCenterY(centerYpx);
      if (!xRange) {
        const centerRowIdx = clampRowIdx(centerYpx);
        const centerRow = rowBounds[centerRowIdx];
        if (centerRow) {
          xRange = {
            minCenter: Number(centerRow.min) + constraintHalfWpx,
            maxCenter: Number(centerRow.max) - constraintHalfWpx,
          };
        }
      }
      if (xRange) {
        if (xRange.minCenter > xRange.maxCenter) {
          const centerXFallback = Number(garmentContour.centerXpx ?? ((xRange.minCenter + xRange.maxCenter) / 2));
          centerXpx = clamp(centerXFallback, Math.min(xRange.minCenter, xRange.maxCenter), Math.max(xRange.minCenter, xRange.maxCenter));
        } else {
          centerXpx = clamp(centerXpx, xRange.minCenter, xRange.maxCenter);
        }
      } else {
        const bounds = garmentBounds || { left: 8, right: 92 };
        const minX = (Number(bounds.left) / 100) * stageWidth + constraintHalfWpx;
        const maxX = (Number(bounds.right) / 100) * stageWidth - constraintHalfWpx;
        centerXpx = clamp(centerXpx, Math.min(minX, maxX), Math.max(minX, maxX));
      }

      const changed = Math.abs(centerXpx - originalCenterXpx) > 0.25 || Math.abs(centerYpx - originalCenterYpx) > 0.25;
      const stripeCollision = hasBBoxStripeCollision(stripeCollisionMap, {
        left: centerXpx - halfWpx,
        right: centerXpx + halfWpx,
        top: centerYpx - halfHpx,
        bottom: centerYpx + halfHpx,
      });
      return {
        dx: ((centerXpx - anchorXpx) / stageWidth) * 100,
        dy: ((centerYpx - anchorYpx) / stageHeight) * 100,
        atLimit: changed,
        stripeCollision,
      };
    }

    const bounds = garmentBounds || { left: 8, right: 92, top: 8, bottom: 92 };
    const insetPctX = (PRINT_CONSTRAINT_INSET_PX / stageWidth) * 100;
    const insetPctY = (PRINT_CONSTRAINT_INSET_PX / stageHeight) * 100;
    const halfW = Math.max(0.4, (widthForConstraint / 2) - insetPctX);
    const halfH = Math.max(0.4, (heightForConstraint / 2) - insetPctY);

    let minCenterX = Number(bounds.left) + halfW;
    let maxCenterX = Number(bounds.right) - halfW;
    let minCenterY = Number(bounds.top) + halfH;
    let maxCenterY = Number(bounds.bottom) - halfH;

    if (minCenterX > maxCenterX) {
      const cx = Number(bounds.centerLeft ?? ((Number(bounds.left) + Number(bounds.right)) / 2));
      minCenterX = cx;
      maxCenterX = cx;
    }
    if (minCenterY > maxCenterY) {
      const cy = Number(bounds.centerTop ?? ((Number(bounds.top) + Number(bounds.bottom)) / 2));
      minCenterY = cy;
      maxCenterY = cy;
    }

    const currentCenterX = anchorLeft + Number(dx || 0);
    const currentCenterY = anchorTop + Number(dy || 0);
    const clampedCenterX = clamp(currentCenterX, minCenterX, maxCenterX);
    const clampedCenterY = clamp(currentCenterY, minCenterY, maxCenterY);
    const nextCenterXpx = ((clampedCenterX / 100) * stageWidth);
    const nextCenterYpx = ((clampedCenterY / 100) * stageHeight);
    const halfWpxForCollision = Math.max(1, (widthForConstraint / 100) * stageWidth * 0.5);
    const halfHpxForCollision = Math.max(1, (heightForConstraint / 100) * stageHeight * 0.5);
    const stripeCollision = hasBBoxStripeCollision(stripeCollisionMap, {
      left: nextCenterXpx - halfWpxForCollision,
      right: nextCenterXpx + halfWpxForCollision,
      top: nextCenterYpx - halfHpxForCollision,
      bottom: nextCenterYpx + halfHpxForCollision,
    });
    return {
      dx: clampedCenterX - anchorLeft,
      dy: clampedCenterY - anchorTop,
      atLimit: clampedCenterX !== currentCenterX || clampedCenterY !== currentCenterY,
      stripeCollision,
    };
  }, [garmentBounds, garmentContour, movementWidthPct, movementHeightPct, anchorLeft, anchorTop, stageMetrics, isText, stripeCollisionMap]);

  const movementByScale = useCallback((scaleValue) => {
    const safeScale = Number(scaleValue) || 100;
    const nextTargetBoxPct = (baseWidthPct * safeScale) / 100;
    const nextEffectiveHeightPct = nextTargetBoxPct;
    const nextEffectiveWidthPct = isText
      ? Math.min(nextTargetBoxPct * 2.2, 95)
      : nextTargetBoxPct * resolvedImageAspectRatio;
    return {
      width: isText ? Math.max(2, nextEffectiveWidthPct * 0.5) : nextEffectiveWidthPct,
      height: isText ? Math.max(2, nextEffectiveHeightPct) : nextEffectiveHeightPct,
    };
  }, [baseWidthPct, isText, resolvedImageAspectRatio]);

  const resolveStripeSafeState = useCallback((candidateState) => {
    const dims = movementByScale(candidateState.scale);
    const baseConstraint = constrainToGarment(candidateState.dx, candidateState.dy, dims.width, dims.height);
    if (!baseConstraint.stripeCollision) {
      return {
        state: {
          ...candidateState,
          dx: baseConstraint.dx,
          dy: baseConstraint.dy,
        },
        autoShifted: false,
        clipped: baseConstraint.atLimit,
      };
    }

    const lastSafe = lastNonCollidingStateRef.current;
    if (lastSafe) {
      const safeConstraint = constrainToGarment(lastSafe.dx, lastSafe.dy, dims.width, dims.height);
      if (!safeConstraint.stripeCollision) {
        return {
          state: {
            ...candidateState,
            dx: safeConstraint.dx,
            dy: safeConstraint.dy,
          },
          autoShifted: true,
          clipped: safeConstraint.atLimit,
        };
      }
    }

    let best = null;
    const radiusSteps = [1.5, 3, 4.5, 6, 7.5, 9, 11, 13, 15, 18, 21];
    const angleStep = Math.PI / 10;
    radiusSteps.forEach((radius) => {
      for (let angle = 0; angle < (Math.PI * 2); angle += angleStep) {
        const probeDx = baseConstraint.dx + (Math.cos(angle) * radius);
        const probeDy = baseConstraint.dy + (Math.sin(angle) * radius);
        const probe = constrainToGarment(probeDx, probeDy, dims.width, dims.height);
        if (probe.stripeCollision) continue;
        const distance = Math.hypot(probe.dx - baseConstraint.dx, probe.dy - baseConstraint.dy);
        if (!best || distance < best.distance) {
          best = { probe, distance };
        }
      }
    });

    if (best?.probe) {
      return {
        state: {
          ...candidateState,
          dx: best.probe.dx,
          dy: best.probe.dy,
        },
        autoShifted: true,
        clipped: best.probe.atLimit,
      };
    }

    return {
      state: {
        ...candidateState,
        dx: baseConstraint.dx,
        dy: baseConstraint.dy,
      },
      autoShifted: false,
      clipped: baseConstraint.atLimit,
    };
  }, [constrainToGarment, movementByScale]);

  useEffect(() => {
    const stageWidth = Number(stageMetrics?.width || 0);
    const stageHeight = Number(stageMetrics?.height || 0);
    if (stageWidth < 20 || stageHeight < 20) return;
    setState((prev) => {
      const constrained = constrainToGarment(prev.dx, prev.dy, movementWidthPct, movementHeightPct);
      if (Math.abs(constrained.dx - prev.dx) < 0.01 && Math.abs(constrained.dy - prev.dy) < 0.01) return prev;
      return { ...prev, dx: constrained.dx, dy: constrained.dy };
    });
  }, [constrainToGarment, movementWidthPct, movementHeightPct, stageMetrics?.width, stageMetrics?.height]);

  const toolsOffsetX = clamp((effectiveWidthPct / 2) + 2, 6, 10);
  // Posición fija: ignora el arrastre (dx/dy) y queda clavado en su centro absoluto.
  const printTop = hasFixedPlacement ? anchorTop : anchorTop + state.dy;
  const printLeft = hasFixedPlacement ? anchorLeft : anchorLeft + state.dx;
  const bbox = {
    left: printLeft,
    top: printTop,
    width: effectiveWidthPct,
    height: Math.max(effectiveHeightPct, 1),
  };

  // Resize por HANDLE de esquina: escala proporcional según la distancia al centro del print.
  // Persiste scale_pct + free_scale=1 (para que un prediseño pase a escalado libre). Funciona
  // para texto y prediseño (el render usa state.scale para ambos).
  const commitResizeScale = useCallback((nextScale) => {
    if (typeof onTransform !== "function") return;
    const scaled = { ...selection, attrs: { ...(selection?.attrs || {}), scale_pct: String(nextScale) } };
    const dims = getSelectionEffectiveDimensionsCm(scaled, {
      aspectRatioOverride: isText ? null : Math.max(0.05, Number(imageAspectRatio || 1)),
      scalePctOverride: nextScale,
    });
    const w = roundUpToHalfCm(dims.width || 0).toFixed(1);
    const h = roundUpToHalfCm(dims.height || 0).toFixed(1);
    onTransform(String(printKey || node.key), {
      offset_x_pct: Number(latestStateRef.current?.dx || 0).toFixed(2),
      offset_y_pct: Number(latestStateRef.current?.dy || 0).toFixed(2),
      scale_pct: String(nextScale),
      rotation_deg: String(Number(latestStateRef.current?.rotation || 0) || 0),
      free_scale: "1",
      width: w, height: h, rendered_width_cm: w, rendered_height_cm: h,
      rendered_aspect_ratio: Number(Math.max(0.05, Number(imageAspectRatio || 1))).toFixed(4),
    }, { phase: "resize" });
  }, [onTransform, selection, isText, imageAspectRatio, printKey, node.key]);

  const onResizePointerDown = (e) => {
    e.stopPropagation();
    e.preventDefault();
    const parent = e.currentTarget.offsetParent || e.currentTarget.parentElement;
    const rect = parent ? parent.getBoundingClientRect() : { left: 0, top: 0, width: 1, height: 1 };
    const cx = rect.left + (printLeft / 100) * rect.width;
    const cy = rect.top + (printTop / 100) * rect.height;
    const startDist = Math.max(8, Math.hypot(e.clientX - cx, e.clientY - cy));
    resizeRef.current = { active: true, pointerId: e.pointerId, cx, cy, startDist, startScale: Number(state.scale) || 100 };
    if (typeof e.currentTarget.setPointerCapture === "function") e.currentTarget.setPointerCapture(e.pointerId);
  };
  const onResizePointerMove = (e) => {
    const r = resizeRef.current;
    if (!r.active || r.pointerId !== e.pointerId) return;
    e.preventDefault();
    e.stopPropagation();
    const dist = Math.max(8, Math.hypot(e.clientX - r.cx, e.clientY - r.cy));
    const nextScale = clamp(Math.round(r.startScale * (dist / r.startDist)), SCALE_MIN, SCALE_MAX);
    setState((prev) => ({ ...prev, scale: nextScale }));
    commitResizeScale(nextScale);
  };
  const onResizePointerEnd = (e) => {
    const r = resizeRef.current;
    if (r.pointerId !== e.pointerId) return;
    resizeRef.current = { ...r, active: false, pointerId: null };
  };
  const containerStyle = {
    position: "absolute",
    top: `${printTop}%`,
    left: `${printLeft}%`,
    transform: `translate(-50%, -50%) rotate(${state.rotation}deg)`,
    zIndex: boostZ ? boostZ : (item.z || 0) + 5,
    width: `${effectiveWidthPct}%`,
    height: `${Math.max(effectiveHeightPct, 1)}%`,
    aspectRatio: undefined,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    pointerEvents: "auto",
    cursor: interactive ? "move" : "default",
    touchAction: "none",
    boxSizing: "border-box",
  };
  const toolsToggleStyle = {
    position: "absolute",
    top: `${printTop - Math.max(effectiveHeightPct / 2, 10) - 2}%`,
    left: `${clamp(printLeft + toolsOffsetX, 4, 96)}%`,
    transform: "translate(-50%, -50%)",
    zIndex: boostZ ? boostZ + 14 : 104,
    width: 32,
    height: 32,
    borderRadius: 999,
    border: "2px solid #d54ab7",
    background: "rgba(255,255,255,0.96)",
    color: "#d54ab7",
    fontSize: 14,
    cursor: "pointer",
    display: "grid",
    placeItems: "center",
    boxShadow: "0 4px 10px rgba(213, 74, 183, 0.28)",
    lineHeight: 1,
  };

  const handleMove = (dxPct, dyPct) => {
    if (!interactive) return;
    const unconstrainedDx = state.dx + dxPct;
    const unconstrainedDy = state.dy + dyPct;
    const constrained = constrainToGarment(unconstrainedDx, unconstrainedDy, movementWidthPct, movementHeightPct);
    if (constrained.atLimit) {
      onClipByMask?.();
    }
    const next = { ...state, dx: constrained.dx, dy: constrained.dy };
    setState(next);
    if (!constrained.stripeCollision) {
      lastNonCollidingStateRef.current = next;
    }
    commit(next, constrained.atLimit, { phase: "move" });
  };

  const handleScale = (delta) => {
    if (!interactive) return;
    if (isFixedPredesign) return;
    const nextScale = clamp(state.scale + delta, SCALE_MIN, SCALE_MAX);
    const nextTargetBoxPct = (baseWidthPct * nextScale) / 100;
    const nextEffectiveHeightPct = nextTargetBoxPct;
    const nextEffectiveWidthPct = isText
      ? Math.min(nextTargetBoxPct * 2.2, 95)
      : nextTargetBoxPct * resolvedImageAspectRatio;
    const nextMovementWidthPct = isText ? Math.max(2, nextEffectiveWidthPct * 0.5) : nextEffectiveWidthPct;
    const nextMovementHeightPct = isText ? Math.max(2, nextEffectiveHeightPct) : nextEffectiveHeightPct;
    const constrained = constrainToGarment(state.dx, state.dy, nextMovementWidthPct, nextMovementHeightPct);
    if (constrained.atLimit) {
      onClipByMask?.();
    }
    const next = { ...state, scale: nextScale, dx: constrained.dx, dy: constrained.dy };
    setState(next);
    if (!constrained.stripeCollision) {
      lastNonCollidingStateRef.current = next;
    }
    commit(next, constrained.atLimit, { phase: "scale" });
  };

  const toggleRotate = () => {
    if (!interactive) return;
    const constrained = constrainToGarment(state.dx, state.dy, movementWidthPct, movementHeightPct);
    const next = { ...state, rotation: (state.rotation + 45) % 360 };
    setState(next);
    if (!constrained.stripeCollision) {
      lastNonCollidingStateRef.current = next;
    }
    commit(next, false, { phase: "rotate" });
  };

  const handleHideTools = useCallback((event) => {
    event?.stopPropagation?.();
    setToolsVisible(false);
  }, []);

  const handleShowTools = useCallback((event) => {
    event?.stopPropagation?.();
    setToolsVisible(true);
  }, []);

  const releaseDragPointerCapture = useCallback((pointerId = null) => {
    const element = dragTargetRef.current;
    const resolvedPointerId = pointerId ?? dragPointerId.current;
    if (!element || resolvedPointerId === null || resolvedPointerId === undefined) return;
    if (typeof element.hasPointerCapture === "function" && element.hasPointerCapture(resolvedPointerId)) {
      element.releasePointerCapture(resolvedPointerId);
    }
  }, []);

  const resetDragInteraction = useCallback((pointerId = null) => {
    releaseDragPointerCapture(pointerId);
    dragging.current = false;
    dragPointerId.current = null;
    dragTargetRef.current = null;
  }, [releaseDragPointerCapture]);

  useEffect(() => {
    const handleGlobalPointerEnd = (event) => {
      resetDragInteraction(event?.pointerId);
    };
    window.addEventListener("pointerup", handleGlobalPointerEnd);
    window.addEventListener("pointercancel", handleGlobalPointerEnd);
    return () => {
      window.removeEventListener("pointerup", handleGlobalPointerEnd);
      window.removeEventListener("pointercancel", handleGlobalPointerEnd);
    };
  }, [resetDragInteraction]);

  const containerEvents = {
    onPointerDown: (e) => {
      if (!interactive) return;
      // Escudo de posición fija: se puede seleccionar (para color/borrar) pero NO arrastrar.
      if (hasFixedPlacement) {
        onSelect?.(printKey || node.key);
        e.stopPropagation();
        e.preventDefault();
        return;
      }
      dragPointerId.current = e.pointerId;
      dragTargetRef.current = e.currentTarget;
      if (typeof e.currentTarget.setPointerCapture === "function") {
        e.currentTarget.setPointerCapture(e.pointerId);
      }
      dragging.current = true;
      lastPos.current = { x: e.clientX, y: e.clientY };
      onSelect?.(printKey || node.key);
      e.stopPropagation();
      e.preventDefault();
    },
    onPointerMove: (e) => {
      if (!interactive) return;
      if (dragPointerId.current !== e.pointerId) return;
      if (!dragging.current) return;
      const dx = ((e.clientX - lastPos.current.x) / e.currentTarget.parentElement.clientWidth) * 100;
      const dy = ((e.clientY - lastPos.current.y) / e.currentTarget.parentElement.clientHeight) * 100;
      handleMove(dx, dy);
      lastPos.current = { x: e.clientX, y: e.clientY };
      e.preventDefault();
    },
    onPointerUp: (e) => {
      if (dragPointerId.current !== e.pointerId) return;
      const latest = latestStateRef.current || state;
      const resolved = resolveStripeSafeState(latest);
      const hasMovedByAutoShift =
        Math.abs(Number(resolved.state.dx || 0) - Number(latest.dx || 0)) > 0.01
        || Math.abs(Number(resolved.state.dy || 0) - Number(latest.dy || 0)) > 0.01;
      if (hasMovedByAutoShift && resolved.autoShifted) {
        onStripeCollision?.();
      }
      if (resolved.clipped) {
        onClipByMask?.();
      }
      setState(resolved.state);
      lastNonCollidingStateRef.current = resolved.state;
      commit(resolved.state, resolved.clipped, { phase: "end" });
      resetDragInteraction(e.pointerId);
    },
    onPointerCancel: (e) => {
      if (dragPointerId.current !== e.pointerId) return;
      resetDragInteraction(e.pointerId);
    },
    onWheel: (e) => {
      if (!interactive) return;
      if (isFixedPredesign) return;
      e.preventDefault();
      const delta = e.deltaY > 0 ? -5 : 5;
      handleScale(delta);
    },
  };

  const printCanvasRef = useRef(null);

  useEffect(() => {
    const canvas = printCanvasRef.current;
    const stageWidth = Math.max(1, Math.round(Number(stageMetrics?.width || 0)));
    const stageHeight = Math.max(1, Math.round(Number(stageMetrics?.height || 0)));
    if (!canvas || stageWidth <= 1 || stageHeight <= 1) return undefined;

    canvas.width = stageWidth;
    canvas.height = stageHeight;
    const ctx = canvas.getContext("2d");
    if (!ctx) return undefined;

    const maskCanvas = garmentMaskCanvas || buildGarmentMask({
      width: stageWidth,
      height: stageHeight,
      garmentContour,
      garmentBounds,
    });
    if (!maskCanvas) return undefined;

    const printLayer = document.createElement("canvas");
    printLayer.width = stageWidth;
    printLayer.height = stageHeight;
    const printCtx = printLayer.getContext("2d");
    if (!printCtx) return undefined;

    const centerX = (printLeft / 100) * stageWidth;
    const centerY = (printTop / 100) * stageHeight;
    const rotationRad = ((Number(state.rotation || 0) % 360) * Math.PI) / 180;

    let cancelled = false;
    const drawPrintClipped = async () => {
      if (isText) {
        const fontId = selection?.attrs?.font;
        const fontDef = fontId ? getFontById(fontId) : null;
        const fontFamily = fontDef?.family || "Arial";
        // Escala por fuente del ABM (%, default 100).
        const fontScalePct = getFontScale(fontDef);
        // fontSize por ALTO real de letra: el print mide effectiveHeightPct del alto del stage;
        // el alto de mayúscula ≈ 0,7·em → factor 1,4 para que la letra mida su alto real.
        const printHeightPx = (effectiveHeightPct / 100) * stageHeight;
        const fontSizePx = clamp(printHeightPx * 1.4 * (fontScalePct / 100), 10, stageHeight * 0.6);
        const textLayout = selection?.attrs?.text_layout === "vertical" ? "vertical" : "horizontal";
        const rawText = String(selection?.attrs?.text || "");
        const renderedLines = textLayout === "vertical"
          ? rawText.replace(/\s+/g, "").split("").filter(Boolean)
          : String(rawText || "").split("\n");
        const textPalette = (selection?.attrs?.color_hexes && selection.attrs.color_hexes.length
          ? selection.attrs.color_hexes
          : (selection?.attrs?.color_hex ? [selection.attrs.color_hex] : ["#222"]))
          .filter(Boolean)
          .slice(0, 2);
        const lineHeight = (textLayout === "vertical" ? 1.05 : 1) * fontSizePx;

        printCtx.save();
        printCtx.translate(centerX, centerY);
        if (rotationRad) printCtx.rotate(rotationRad);
        printCtx.font = `${Math.round(fontSizePx)}px '${fontFamily}', sans-serif`;
        printCtx.textAlign = "center";
        printCtx.textBaseline = "middle";
        if (textLayout === "vertical") {
          if (wantsTextOutline && textPalette.length > 1) {
            drawEmbroideryText({
              ctx: printCtx,
              lines: renderedLines,
              lineHeight,
              fillColor: textPalette[0] || "#ffffff",
              strokeColor: textPalette[1] || "#1f2a3d",
              textLayout,
            });
          } else {
            let paletteCursor = 0;
            renderedLines.forEach((line, idx) => {
              const chars = Array.from(String(line || ""));
              const y = (idx - (renderedLines.length - 1) / 2) * lineHeight;
              if (!chars.length) {
                printCtx.fillStyle = textPalette[paletteCursor % textPalette.length] || "#222";
                printCtx.fillText(" ", 0, y);
                paletteCursor += 1;
                return;
              }
              chars.forEach((char) => {
                printCtx.fillStyle = textPalette[paletteCursor % textPalette.length] || "#222";
                printCtx.fillText(char, 0, y);
                paletteCursor += 1;
              });
            });
          }
        } else {
          // Texto horizontal → forma (recta/diagonal/arcoíris/curva) + tamaño por renglón.
          const lineSizes = Array.isArray(selection?.attrs?.line_sizes) ? selection.attrs.line_sizes : [];
          const shapedLines = renderedLines.map((t, i) => ({
            text: t,
            size: Number(lineSizes[i]) > 0 ? Number(lineSizes[i]) : 1,
          }));
          drawShapedText(printCtx, {
            lines: shapedLines,
            baseFontSize: fontSizePx,
            fontFamily,
            shape: selection?.attrs?.text_shape || "recta",
            curvature: Number.isFinite(Number(selection?.attrs?.text_curvature))
              ? Number(selection.attrs.text_curvature)
              : 55,
            palette: textPalette,
            embroidery: wantsTextOutline && textPalette.length > 1,
          });
        }
        printCtx.restore();
      } else if (src) {
        const image = await loadCanvasImage(src).catch(() => null);
        if (!image || cancelled) return;
        const targetWidthPx = (effectiveWidthPct / 100) * stageWidth;
        const targetHeightPx = (effectiveHeightPct / 100) * stageHeight;
        const sourceRect = imageOpaqueBounds || {
          x: 0,
          y: 0,
          width: Number(image.naturalWidth || image.width || 1),
          height: Number(image.naturalHeight || image.height || 1),
        };
        const sourceW = Math.max(1, Number(sourceRect.width || image.naturalWidth || image.width || 1));
        const sourceH = Math.max(1, Number(sourceRect.height || image.naturalHeight || image.height || 1));
        const fitScale = Math.min(targetWidthPx / sourceW, targetHeightPx / sourceH);
        const drawW = Math.max(1, sourceW * fitScale);
        const drawH = Math.max(1, sourceH * fitScale);

        printCtx.save();
        printCtx.translate(centerX, centerY);
        if (rotationRad) printCtx.rotate(rotationRad);
        if (canTintPredesign && (selection?.attrs?.color_hex || selection?.attrs?.color_hexes?.length)) {
          const off = document.createElement("canvas");
          off.width = Math.max(1, Math.round(drawW));
          off.height = Math.max(1, Math.round(drawH));
          const offCtx = off.getContext("2d");
          if (offCtx) {
            offCtx.drawImage(
              image,
              Number(sourceRect.x || 0),
              Number(sourceRect.y || 0),
              sourceW,
              sourceH,
              0,
              0,
              off.width,
              off.height
            );
            offCtx.globalCompositeOperation = "source-in";
            if (isEmbroidery && selection?.attrs?.color_hexes && selection.attrs.color_hexes.length > 1) {
              drawEmbroideryTintedPredesign(
                printCtx,
                image,
                drawW,
                drawH,
                selection.attrs.color_hexes[0] || "#ffffff",
                selection.attrs.color_hexes[1] || "#1f2a3d",
                sourceRect
              );
              printCtx.restore();
              if (cancelled) return;
              printCtx.globalCompositeOperation = "destination-in";
              printCtx.drawImage(maskCanvas, 0, 0, stageWidth, stageHeight);
              printCtx.globalCompositeOperation = "source-over";
              ctx.clearRect(0, 0, stageWidth, stageHeight);
              ctx.drawImage(printLayer, 0, 0, stageWidth, stageHeight);
              return;
            } else if (selection?.attrs?.color_hexes && selection.attrs.color_hexes.length > 1) {
              const first = selection.attrs.color_hexes[0];
              offCtx.fillStyle = first;
            } else {
              offCtx.fillStyle = selection?.attrs?.color_hex || "#ffffff";
            }
            offCtx.fillRect(0, 0, off.width, off.height);
            offCtx.globalCompositeOperation = "source-over";
            printCtx.drawImage(off, -drawW / 2, -drawH / 2, drawW, drawH);
          }
        } else {
          printCtx.drawImage(
            image,
            Number(sourceRect.x || 0),
            Number(sourceRect.y || 0),
            sourceW,
            sourceH,
            -drawW / 2,
            -drawH / 2,
            drawW,
            drawH
          );
        }
        printCtx.restore();
      }

      if (cancelled) return;
      printCtx.globalCompositeOperation = "destination-in";
      printCtx.drawImage(maskCanvas, 0, 0, stageWidth, stageHeight);
      printCtx.globalCompositeOperation = "source-over";
      ctx.clearRect(0, 0, stageWidth, stageHeight);
      ctx.drawImage(printLayer, 0, 0, stageWidth, stageHeight);
    };

    drawPrintClipped();
    return () => {
      cancelled = true;
    };
  }, [
    stageMetrics?.width,
    stageMetrics?.height,
    garmentMaskCanvas,
    garmentContour,
    garmentBounds,
    isText,
    src,
    canTintPredesign,
    isEmbroidery,
    selection?.attrs?.text,
    selection?.attrs?.font,
    selection?.attrs?.text_layout,
    selection?.attrs?.text_shape,
    selection?.attrs?.text_curvature,
    selection?.attrs?.line_sizes,
    selection?.attrs?.color_hex,
    selection?.attrs?.color_hexes,
    selection?.attrs?.text_border,
    wantsTextOutline,
    effectiveWidthPct,
    effectiveHeightPct,
    imageOpaqueBounds,
    printLeft,
    printTop,
    state.rotation,
  ]);

  if (shouldHide) return null;

  if (!node?.Item) return null;

  return (
    <>
      <canvas
        ref={printCanvasRef}
        style={{
          position: "absolute",
          inset: 0,
          width: "100%",
          height: "100%",
          pointerEvents: "none",
          zIndex: boostZ ? boostZ : (item.z || 0) + 5,
        }}
      />
      {interactive && active && (
        <div
          style={{
            position: "absolute",
            top: `${bbox.top}%`,
            left: `${bbox.left}%`,
            width: `${bbox.width}%`,
            height: `${bbox.height}%`,
            transform: "translate(-50%, -50%)",
            border: "1px dashed rgba(0,113,206,0.6)",
            borderRadius: 8,
            pointerEvents: "none",
            zIndex: (item.z || 0) + 4,
          }}
        />
      )}
      <div
        style={containerStyle}
        title={selection?.print?.factory_name || selection?.print?.nice_name || selection?.print?.name || "Diseño"}
        {...containerEvents}
      />
      {interactive && active && !hasFixedPlacement && (
        <div
          onPointerDown={onResizePointerDown}
          onPointerMove={onResizePointerMove}
          onPointerUp={onResizePointerEnd}
          onPointerCancel={onResizePointerEnd}
          aria-label="Cambiar tamaño"
          style={{
            position: "absolute",
            top: `${clamp(printTop + bbox.height / 2, 1, 99)}%`,
            left: `${clamp(printLeft + bbox.width / 2, 1, 99)}%`,
            transform: "translate(-50%, -50%)",
            width: 20,
            height: 20,
            borderRadius: "50%",
            background: "#e658c5",
            border: "2px solid #fff",
            boxShadow: "0 1px 5px rgba(0,0,0,0.35)",
            cursor: "nwse-resize",
            touchAction: "none",
            pointerEvents: "auto",
            zIndex: boostZ ? boostZ + 12 : 120,
          }}
        />
      )}
      {/* Badge tocable (✎) sobre el print cuando NO está seleccionado: da el "punto para editar". */}
      {interactive && !active && (
        <button
          type="button"
          onPointerDown={(e) => { e.stopPropagation(); e.preventDefault(); onSelect?.(printKey || node.key); }}
          aria-label="Editar diseño"
          style={{
            position: "absolute",
            top: `${clamp(printTop - bbox.height / 2, 1, 99)}%`,
            left: `${clamp(printLeft + bbox.width / 2, 1, 99)}%`,
            transform: "translate(-50%, -50%)",
            width: 26, height: 26, borderRadius: "50%",
            background: "#2f6fed", color: "#fff", border: "2px solid #fff",
            boxShadow: "0 1px 5px rgba(0,0,0,0.35)", cursor: "pointer",
            touchAction: "manipulation", pointerEvents: "auto",
            display: "flex", alignItems: "center", justifyContent: "center", fontSize: 12,
            zIndex: boostZ ? boostZ + 13 : 121,
          }}
        >
          <i className="bi bi-pencil-fill" aria-hidden="true" />
        </button>
      )}
      {/* Popover de edición del print seleccionado: color/es + eliminar (como el de las tiras).
          En escudos de POSICIÓN FIJA no se muestra: no se mueve ni se edita desde el canvas (el
          color va en el panel del wizard). */}
      {interactive && active && !hasFixedPlacement && (onDeletePrint || onEditColor) && (
        <div
          onPointerDown={(e) => e.stopPropagation()}
          style={{
            position: "absolute",
            top: `${clamp(printTop - bbox.height / 2 - 3, 4, 92)}%`,
            left: `${clamp(printLeft, 10, 90)}%`,
            transform: "translate(-50%, -100%)",
            display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap",
            maxWidth: 230, padding: "6px 8px", borderRadius: 12,
            background: "#fff", border: "1px solid #cdd9ea",
            boxShadow: "0 8px 22px rgba(16,35,63,0.22)",
            zIndex: boostZ ? boostZ + 22 : 130,
          }}
        >
          {onEditColor && !isImageUpload && (allColors || []).slice(0, 8).map((c) => {
            const code = c.code || c.hex || "#1d4ed8";
            return (
              <button
                key={`pc-${c.id ?? code}`}
                type="button"
                aria-label={c.name || c.nname || "Color"}
                onPointerDown={(e) => { e.stopPropagation(); e.preventDefault(); onEditColor(String(printKey || node.key), c, 1); }}
                style={{ width: 20, height: 20, borderRadius: "50%", background: code, border: "2px solid #fff", boxShadow: "0 0 0 1px #cdd9ea", cursor: "pointer", padding: 0 }}
              />
            );
          })}
          {onDeletePrint && (
            <button
              type="button"
              aria-label="Eliminar diseño"
              onPointerDown={(e) => { e.stopPropagation(); e.preventDefault(); onDeletePrint(String(printKey || node.key)); }}
              style={{ width: 26, height: 26, borderRadius: 8, border: 0, background: "#fbe4e4", color: "#a12b2b", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", flex: "0 0 auto" }}
            >
              <i className="bi bi-trash3-fill" aria-hidden="true" />
            </button>
          )}
          <button
            type="button"
            aria-label="Cerrar edición"
            onPointerDown={(e) => { e.stopPropagation(); e.preventDefault(); onSelect?.(""); }}
            style={{ width: 26, height: 26, borderRadius: 8, border: "1px solid #cdd9ea", background: "#f1f5f9", color: "#475569", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", flex: "0 0 auto" }}
          >
            <i className="bi bi-check-lg" aria-hidden="true" />
          </button>
        </div>
      )}
      {interactive && active && toolsVisible && !hasFixedPlacement && (
        <div
          className={styles.zoomSliderContainer}
          style={{
            top: `${printTop}%`,
            left: `${clamp(printLeft + toolsOffsetX, 4, 96)}%`,
            zIndex: boostZ ? boostZ + 10 : 100,
            background: "#ffffff",
            padding: "8px 4px",
            borderRadius: 8,
            boxShadow: "0 2px 8px rgba(0,0,0,0.15)",
          }}
        >
          {/* El tamaño se ajusta SOLO con el handle de la esquina (se quitó el slider vertical
              para no duplicar). Acá queda solo rotar. */}
          <button
            type="button"
            onClick={toggleRotate}
            aria-label="Rotar diseño 45 grados"
            style={{
              marginTop: 6,
              border: "1px solid #0071ce",
              background: "#fff",
              color: "#0071ce",
              borderRadius: 6,
              fontSize: 10,
              fontWeight: 700,
              padding: "4px 6px",
              cursor: "pointer",
            }}
          >
            <i className="bi bi-arrow-clockwise" aria-hidden="true" />
          </button>
        </div>
      )}
      {interactive && active && !hasFixedPlacement && (
        <button
          type="button"
          onPointerDown={toolsVisible ? handleHideTools : handleShowTools}
          aria-label={toolsVisible ? "Ocultar herramientas" : "Mostrar herramientas"}
          style={toolsToggleStyle}
        >
          <i className={`bi ${toolsVisible ? "bi-eye-slash" : "bi-eye"}`} aria-hidden="true" />
        </button>
      )}
    </>
  );
};

const resolveSide = (node) => {
  if (!node || String(node.type || node.master_part_type) !== "6") return null;
  const item = node.Item || {};
  const side = resolvePlacementSide(item?.placement ?? item?.posicion ?? null);
  if (!side) return null;
  if (side === "front") return 0;
  if (side === "back") return 1;
  return null;
};

const PreviewSection = ({
  selectedParts,
  root,
  view = "front",
  selectedPrints = {},
  allColors = [],
  showPrints = true,
  onTransformPrint,
  onPrintClipWarning = null,
  activePrintKey,
  onSelectPrint,
  onDeletePrint = null,
  onEditPrintColor = null,
  partHotspots = [],
  onSelectPart,
  showHotspots = false,
  canFlip = false,
  onToggleView,
  onResetDesign,
  currentStep = 1,
  totalSteps = 3,
  enablePartPaint = false,
  enableStripeEditing = false,
  stripeColors = [],
  saving = false,
  nextDisabled = false,
  saveDisabled = false,
  onPrev,
  onNext,
  onSave,
  enablePrintEditing = true,
  stripeDrawingsBySide = {},
  stripeDrawEditor = null,
  onSetStripeDrawEditor = null,
  onUpdateStripeDrawingsBySide = null,
  customCutsBySide = {},
  customCutEditor = null,
  onSetCustomCutEditor = null,
  onUpdateCustomCutsBySide = null,
  customCutZoneColors = {},
  onCustomCutRuntimeUpdate = null,
  onPreviewPaintCustomCutZone = null,
  highlightedPartKey = null,
  sparklePartKeys = [],
  sparkleToken = 0,
  highlightedCustomCutZoneId = null,
  sparkleCustomCutZoneIds = [],
  sparkleCustomCutToken = 0,
}) => {
  const activePrintLocationKey = String(activePrintKey || "").split("::")[0];
  const stageRef = useRef(null);
  const [stageSize, setStageSize] = useState({ width: 0, height: 0 });
  const [garmentContour, setGarmentContour] = useState(null);
  const [stripeCollisionMap, setStripeCollisionMap] = useState(null);
  const [stripeSleeveCollisionMap, setStripeSleeveCollisionMap] = useState(null);
  const [regionMaskCanvases, setRegionMaskCanvases] = useState({});
  const [zMaskCanvases, setZMaskCanvases] = useState({});
  const [stripeSnapTargetMap, setStripeSnapTargetMap] = useState(null);
  const [stripeWarningVisible, setStripeWarningVisible] = useState(false);
  const [paintTipVisible, setPaintTipVisible] = useState(true); // tip "tocá una parte": se va al primer toque
  // Borrador (trazo en curso, siempre arriba) + un canvas por z de tira (las tiras se
  // dibujan al z de la parte donde arrancan, para que partes con z mayor las tapen).
  const stripeDraftCanvasRef = useRef(null);
  const stripeBucketCanvasRefs = useRef(new Map());
  const customCutCanvasRef = useRef(null);
  const customCutFxCanvasRef = useRef(null);
  const stripeWarningTimeoutRef = useRef(null);
  const stripeWarningCooldownRef = useRef(0);
  const [customCutRuntime, setCustomCutRuntime] = useState(null);
  const [customCutSparkleVisible, setCustomCutSparkleVisible] = useState(false);
  const stageMetrics = useMemo(
    () => ({ width: stageSize.width, height: stageSize.height, baseSize: LEGACY_BASE_SIZE }),
    [stageSize.width, stageSize.height]
  );
  const activeStripeStrokes = useMemo(
    () => Array.isArray(stripeDrawingsBySide?.[view]) ? stripeDrawingsBySide[view] : [],
    [stripeDrawingsBySide, view]
  );
  const activeCustomCuts = useMemo(
    () => Array.isArray(customCutsBySide?.[view]) ? customCutsBySide[view].map((cut) => normalizeCustomCutStroke(cut)) : [],
    [customCutsBySide, view]
  );
  const hasActiveCustomCuts = activeCustomCuts.length > 0;
  const activeCustomCutZoneOverlaysByPart = useMemo(
    () => {
      const map = {};
      const zones = Array.isArray(customCutRuntime?.zones) ? customCutRuntime.zones : [];
      zones.forEach((zone) => {
        if (!zone?.color?.code || !zone?.maskCanvas) return;
        const partKeys = Array.isArray(zone?.partKeys) ? zone.partKeys : [];
        partKeys.forEach((partKey) => {
          const resolvedKey = String(partKey || "");
          if (!resolvedKey) return;
          if (!Array.isArray(map[resolvedKey])) {
            map[resolvedKey] = [];
          }
          map[resolvedKey].push(zone);
        });
      });
      return map;
    },
    [customCutRuntime]
  );
  const [draftStrokePoints, setDraftStrokePoints] = useState([]);
  const draftStrokePointsRef = useRef([]);
  const drawingRef = useRef(false);
  const [selectedStrokeId, setSelectedStrokeId] = useState(null);
  const [selectedStrokeAnchor, setSelectedStrokeAnchor] = useState(null);
  // Al salir del paso de tiras, cerrar el popover de edición (si no, queda "colgado" tapando la prenda).
  useEffect(() => {
    if (enableStripeEditing) return;
    setSelectedStrokeId(null);
    setSelectedStrokeAnchor(null);
  }, [enableStripeEditing]);
  const [stripeEditColorsExpanded, setStripeEditColorsExpanded] = useState(false);
  useEffect(() => {
    if (!selectedStrokeId) setStripeEditColorsExpanded(false);
  }, [selectedStrokeId]);
  const [draftCustomCutPoints, setDraftCustomCutPoints] = useState([]);
  const draftCustomCutPointsRef = useRef([]);
  const drawingCustomCutRef = useRef(false);
  const [selectedCustomCutId, setSelectedCustomCutId] = useState(null);
  const [selectedCustomCutAnchor, setSelectedCustomCutAnchor] = useState(null);
  const [customCutCursorPos, setCustomCutCursorPos] = useState(null);
  const moveStripeRef = useRef({
    active: false,
    strokeId: null,
    pointerId: null,
    lastPoint: null,
  });
  const moveCustomCutRef = useRef({
    active: false,
    cutId: null,
    pointerId: null,
    lastPoint: null,
  });
  useEffect(() => {
    draftStrokePointsRef.current = draftStrokePoints;
  }, [draftStrokePoints]);
  useEffect(() => {
    draftCustomCutPointsRef.current = draftCustomCutPoints;
  }, [draftCustomCutPoints]);
  useEffect(() => {
    if (!stripeDrawEditor?.enabled) {
      drawingRef.current = false;
      setDraftStrokePoints([]);
      moveStripeRef.current = {
        active: false,
        strokeId: null,
        pointerId: null,
        lastPoint: null,
      };
    }
  }, [stripeDrawEditor?.enabled, view]);

  useEffect(() => {
    if (!customCutEditor?.enabled) {
      drawingCustomCutRef.current = false;
      setDraftCustomCutPoints([]);
      setCustomCutCursorPos(null);
      moveCustomCutRef.current = {
        active: false,
        cutId: null,
        pointerId: null,
        lastPoint: null,
      };
    }
  }, [customCutEditor?.enabled, view]);

  useEffect(() => {
    if (hasActiveCustomCuts) return;
    drawingCustomCutRef.current = false;
    setDraftCustomCutPoints([]);
    draftCustomCutPointsRef.current = [];
    setSelectedCustomCutId(null);
    setSelectedCustomCutAnchor(null);
    setCustomCutCursorPos(null);
    moveCustomCutRef.current = {
      active: false,
      cutId: null,
      pointerId: null,
      lastPoint: null,
    };
  }, [hasActiveCustomCuts]);

  useEffect(() => {
    if (!sparkleCustomCutToken || !Array.isArray(sparkleCustomCutZoneIds) || !sparkleCustomCutZoneIds.length) {
      setCustomCutSparkleVisible(false);
      return undefined;
    }
    setCustomCutSparkleVisible(true);
    const timeoutId = window.setTimeout(() => {
      setCustomCutSparkleVisible(false);
    }, 650);
    return () => window.clearTimeout(timeoutId);
  }, [sparkleCustomCutToken, sparkleCustomCutZoneIds]);

  useEffect(() => {
    setSelectedStrokeId(null);
    setSelectedStrokeAnchor(null);
    drawingRef.current = false;
    setDraftStrokePoints([]);
    setSelectedCustomCutId(null);
    setSelectedCustomCutAnchor(null);
    setDraftCustomCutPoints([]);
    moveStripeRef.current = {
      active: false,
      strokeId: null,
      pointerId: null,
      lastPoint: null,
    };
    moveCustomCutRef.current = {
      active: false,
      cutId: null,
      pointerId: null,
      lastPoint: null,
    };
  }, [view]);

  const resolveNormalizedPoint = useCallback((clientX, clientY) => {
    const element = stageRef.current;
    if (!element) return null;
    const rect = element.getBoundingClientRect();
    if (!rect.width || !rect.height) return null;
    return [
      clamp((clientX - rect.left) / rect.width, 0, 1),
      clamp((clientY - rect.top) / rect.height, 0, 1),
    ];
  }, []);

  useEffect(() => {
    if (!selectedStrokeId) return;
    const selected = (activeStripeStrokes || []).find((stroke) => String(stroke?.id || "") === String(selectedStrokeId));
    if (!selected) {
      setSelectedStrokeId(null);
      setSelectedStrokeAnchor(null);
      return;
    }
    const points = Array.isArray(selected.points_norm) ? selected.points_norm : [];
    if (!points.length) return;
    const sx = points.reduce((acc, p) => acc + Number(p?.[0] || 0), 0) / points.length;
    const sy = points.reduce((acc, p) => acc + Number(p?.[1] || 0), 0) / points.length;
    setSelectedStrokeAnchor([clamp(sx, 0, 1), clamp(sy, 0, 1)]);
  }, [selectedStrokeId, activeStripeStrokes]);

  useEffect(() => {
    if (!selectedCustomCutId) return;
    const selected = (customCutRuntime?.renderedCuts || []).find((cut) => String(cut?.id || "") === String(selectedCustomCutId));
    if (!selected) {
      setSelectedCustomCutId(null);
      setSelectedCustomCutAnchor(null);
      return;
    }
    const points = Array.isArray(selected.points_norm) ? selected.points_norm : [];
    if (!points.length) return;
    const sx = points.reduce((acc, point) => acc + Number(point?.[0] || 0), 0) / points.length;
    const sy = points.reduce((acc, point) => acc + Number(point?.[1] || 0), 0) / points.length;
    setSelectedCustomCutAnchor([clamp(sx, 0, 1), clamp(sy, 0, 1)]);
  }, [selectedCustomCutId, customCutRuntime]);

  useEffect(() => {
    const element = stageRef.current;
    if (!element) return undefined;

    const updateSize = () => {
      const rect = element.getBoundingClientRect();
      const next = {
        width: rect.width || 0,
        height: rect.height || 0,
      };
      setStageSize((prev) => {
        if (Math.abs(prev.width - next.width) < 0.5 && Math.abs(prev.height - next.height) < 0.5) {
          return prev;
        }
        return next;
      });
    };

    updateSize();
    if (typeof ResizeObserver === "undefined") {
      window.addEventListener("resize", updateSize);
      return () => window.removeEventListener("resize", updateSize);
    }

    const observer = new ResizeObserver(() => updateSize());
    observer.observe(element);
    return () => observer.disconnect();
  }, []);

  const layers = useMemo(() => {
    const list = [];
    (selectedParts || []).forEach((p) => {
      const item = p.Item || {};
      const z = Number(item.z || item.z_index || 0);
      const colorHex = p.color?.code || p.color?.hex || null;
      const patternUrl = p.fullprintIcon || null;
      const isStripePart = isStripeLikePart(p);
      if (view === "front") {
        if (item.img_front_t) list.push({ kind: "mask", img: item.img_front_t, zIndex: z, colorHex, patternUrl, item, forceSolidFill: false, partKey: String(p?.key || ""), partLabel: String(item?.nname || item?.name || p?.name || ""), node: p });
        else if (item.img_front && colorHex && isStripePart) list.push({ kind: "mask", img: item.img_front, zIndex: z, colorHex, patternUrl: null, item, forceSolidFill: true, partKey: String(p?.key || ""), partLabel: String(item?.nname || item?.name || p?.name || ""), node: p });
        if (item.img_front) list.push({ kind: "detail", img: item.img_front, zIndex: z + 2, colorHex: null, patternUrl: null, item, partKey: String(p?.key || ""), partLabel: String(item?.nname || item?.name || p?.name || ""), node: p });
      } else {
        if (item.img_back_t) list.push({ kind: "mask", img: item.img_back_t, zIndex: z, colorHex, patternUrl, item, forceSolidFill: false, partKey: String(p?.key || ""), partLabel: String(item?.nname || item?.name || p?.name || ""), node: p });
        else if (item.img_back && colorHex && isStripePart) list.push({ kind: "mask", img: item.img_back, zIndex: z, colorHex, patternUrl: null, item, forceSolidFill: true, partKey: String(p?.key || ""), partLabel: String(item?.nname || item?.name || p?.name || ""), node: p });
        if (item.img_back) list.push({ kind: "detail", img: item.img_back, zIndex: z + 2, colorHex: null, patternUrl: null, item, partKey: String(p?.key || ""), partLabel: String(item?.nname || item?.name || p?.name || ""), node: p });
      }
    });
    return list.sort((a, b) => a.zIndex - b.zIndex);
  }, [selectedParts, view]);
  const stripeLayerZ = useMemo(() => {
    const maskZ = layers
      .filter((layer) => layer.kind === "mask")
      .map((layer) => Number(layer.zIndex || 0));
    const baseMaskZ = maskZ.length ? Math.max(...maskZ) : 1;
    return baseMaskZ + 1;
  }, [layers]);
  // Agrupa las tiras por z de render. Cada tira renderiza al z de su parte de arranque + 3
  // (arriba del detalle de esa parte, z+2, pero debajo de partes con z mayor). Tiras sin z
  // capturado (dibujos viejos) van arriba de todo, como antes.
  const stripeZLayers = useMemo(() => {
    const buckets = new Map();
    (activeStripeStrokes || []).forEach((stroke) => {
      const raw = Number(stroke?.z);
      const renderZ = Number.isFinite(raw) ? raw + 3 : (stripeLayerZ + 2);
      if (!buckets.has(renderZ)) buckets.set(renderZ, []);
      buckets.get(renderZ).push(stroke);
    });
    return Array.from(buckets.entries())
      .map(([z, strokes]) => ({ z, strokes }))
      .sort((a, b) => a.z - b.z);
  }, [activeStripeStrokes, stripeLayerZ]);
  // Marcadores T1/T2… centrados en cada tira (para tocar y editar). Se ubican en el centroide
  // del trazo (mismo punto que el ancla del popover), en % del stage.
  const stripeBadges = useMemo(() => {
    return (activeStripeStrokes || []).map((stroke, idx) => {
      const pts = Array.isArray(stroke?.points_norm) ? stroke.points_norm : [];
      if (pts.length < 2) return null;
      const cx = pts.reduce((acc, p) => acc + Number(p?.[0] || 0), 0) / pts.length;
      const cy = pts.reduce((acc, p) => acc + Number(p?.[1] || 0), 0) / pts.length;
      return {
        id: String(stroke?.id || `stripe-${idx}`),
        label: `T${idx + 1}`,
        cx: clamp(cx, 0, 1),
        cy: clamp(cy, 0, 1),
      };
    }).filter(Boolean);
  }, [activeStripeStrokes]);
  const stripeSnapSourceLayers = useMemo(
    () => Array.from(
      new Set(
        layers
          .filter((layer) => layer.kind === "detail" || layer.kind === "mask")
          .map((layer) => String(layer.img || ""))
          .filter(Boolean)
      )
    ),
    [layers]
  );

  const stripeCollisionLayers = useMemo(() => {
    const list = [];
    (selectedParts || []).forEach((p) => {
      if (!isStripeLikePart(p)) return;
      const item = p.Item || {};
      if (view === "front") {
        if (item.img_front_t) list.push(item.img_front_t);
        else if (item.img_front) list.push(item.img_front);
      } else {
        if (item.img_back_t) list.push(item.img_back_t);
        else if (item.img_back) list.push(item.img_back);
      }
    });
    return Array.from(new Set(list.filter(Boolean).map((img) => String(img))));
  }, [selectedParts, view]);
  const stripeSleeveCollisionLayers = useMemo(() => {
    const list = [];
    (selectedParts || []).forEach((p) => {
      if (!isStripeLikePart(p) || !isSleeveLikePart(p)) return;
      const item = p.Item || {};
      if (view === "front") {
        if (item.img_front_t) list.push(item.img_front_t);
        else if (item.img_front) list.push(item.img_front);
      } else {
        if (item.img_back_t) list.push(item.img_back_t);
        else if (item.img_back) list.push(item.img_back);
      }
    });
    return Array.from(new Set(list.filter(Boolean).map((img) => String(img))));
  }, [selectedParts, view]);

  // Máscaras de las partes agrupadas por REGIÓN (para recortar la tira a la región donde arranca).
  // Excluye las partes "tira" (placeholders); usa la máscara del lado visible.
  const regionLayerImages = useMemo(() => {
    const byRegion = {};
    (selectedParts || []).forEach((p) => {
      if (isStripeLikePart(p)) return;
      const item = p.Item || {};
      const img = view === "front"
        ? (item.img_front_t || item.img_front)
        : (item.img_back_t || item.img_back);
      if (!img) return;
      const region = partRegion(p);
      if (!region) return; // null = avio/tira -> no es region de recorte
      (byRegion[region] = byRegion[region] || []).push(String(img));
    });
    Object.keys(byRegion).forEach((r) => {
      byRegion[r] = Array.from(new Set(byRegion[r]));
    });
    return byRegion;
  }, [selectedParts, view]);

  // Imágenes de parte agrupadas por z (para hit-test de apilado). El z de apilado es INDEPENDIENTE
  // de la región: la tira hereda el z de la parte que queda MÁS ARRIBA en su punto de arranque.
  // Así funciona aunque las regiones no estén clasificadas (recorte y apilado son cosas distintas).
  const zLayerImages = useMemo(() => {
    const byZ = {};
    (selectedParts || []).forEach((p) => {
      if (isStripeLikePart(p)) return;
      const item = p.Item || {};
      const img = view === "front"
        ? (item.img_front_t || item.img_front)
        : (item.img_back_t || item.img_back);
      if (!img) return;
      const z = Number(item.z || item.z_index || 0);
      if (!Number.isFinite(z)) return;
      (byZ[z] = byZ[z] || []).push(String(img));
    });
    Object.keys(byZ).forEach((z) => {
      byZ[z] = Array.from(new Set(byZ[z]));
    });
    return byZ;
  }, [selectedParts, view]);

  useEffect(() => {
    const width = Math.round(stageSize.width || 0);
    const height = Math.round(stageSize.height || 0);
    if (width <= 0 || height <= 0 || !layers.length) {
      setGarmentContour(null);
      return;
    }

    let cancelled = false;
    const load = (src) => new Promise((resolve, reject) => {
      const img = new Image();
      img.crossOrigin = "anonymous";
      img.onload = () => resolve(img);
      img.onerror = reject;
      img.src = src;
    });

    (async () => {
      try {
        const urls = layers.map((layer) => getImgUrl(layer?.img)).filter(Boolean);
        const uniqueUrls = Array.from(new Set(urls));
        if (!uniqueUrls.length) {
          if (!cancelled) setGarmentContour(null);
          return;
        }
        const images = await Promise.all(uniqueUrls.map((url) => load(url).catch(() => null)));
        if (cancelled) return;
        const validImages = images.filter(Boolean);
        if (!validImages.length) {
          setGarmentContour(null);
          return;
        }

        const canvas = document.createElement("canvas");
        canvas.width = width;
        canvas.height = height;
        const ctx = canvas.getContext("2d");
        if (!ctx) {
          setGarmentContour(null);
          return;
        }

        ctx.clearRect(0, 0, width, height);
        validImages.forEach((img) => drawContainImage(ctx, img, width, height));
        const imageData = ctx.getImageData(0, 0, width, height).data;
        const rowBounds = new Array(height).fill(null);
        let topPx = null;
        let bottomPx = null;
        let leftPx = width;
        let rightPx = 0;

        for (let y = 0; y < height; y += 1) {
          let rowMin = null;
          let rowMax = null;
          for (let x = 0; x < width; x += 1) {
            const alpha = imageData[((y * width + x) * 4) + 3];
            if (alpha <= 8) continue;
            if (rowMin === null) rowMin = x;
            rowMax = x;
          }
          if (rowMin !== null && rowMax !== null) {
            rowBounds[y] = { min: rowMin, max: rowMax };
            if (topPx === null) topPx = y;
            bottomPx = y;
            leftPx = Math.min(leftPx, rowMin);
            rightPx = Math.max(rightPx, rowMax);
          }
        }

        if (topPx === null || bottomPx === null) {
          setGarmentContour(null);
          return;
        }

        const centerYpx = Math.round((topPx + bottomPx) / 2);
        const centerRow = rowBounds[Math.max(0, Math.min(height - 1, centerYpx))];
        const centerXpx = centerRow
          ? Math.round((centerRow.min + centerRow.max) / 2)
          : Math.round((leftPx + rightPx) / 2);

        setGarmentContour({
          width,
          height,
          rowBounds,
          topPx,
          bottomPx,
          leftPx,
          rightPx,
          centerXpx,
          centerYpx,
        });
      } catch (_error) {
        if (!cancelled) setGarmentContour(null);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [layers, stageSize.width, stageSize.height]);

  const { printNodes, maxZ } = useMemo(() => {
    const prints = [];
    const traverse = (node) => {
      if (!node) return;
      if (String(node.type || node.master_part_type) === "6") {
        prints.push({ node });
      }
      (node.children || []).forEach(traverse);
    };
    traverse(root);
    const z = Math.max(...layers.map(l => l.zIndex || 0), 0);
    const seen = new Set();
    const uniquePrintNodes = prints
      .map((p) => p.node)
      .filter((node) => {
        const key = String(node?.key || "");
        if (!key || seen.has(key)) return false;
        seen.add(key);
        return true;
      });
    return { printNodes: uniquePrintNodes, maxZ: z };
  }, [root, layers]);
  const garmentBounds = useMemo(() => {
    const fallback = { left: 14, right: 86, top: 8, bottom: 94, centerLeft: 50, centerTop: 51 };
    if (garmentContour) {
      const width = Math.max(1, Number(garmentContour.width || stageMetrics.width || 1));
      const height = Math.max(1, Number(garmentContour.height || stageMetrics.height || 1));
      const left = clamp((Number(garmentContour.leftPx || 0) / width) * 100, 2, 98);
      const right = clamp((Number(garmentContour.rightPx || width) / width) * 100, 2, 98);
      const top = clamp((Number(garmentContour.topPx || 0) / height) * 100, 2, 98);
      const bottom = clamp((Number(garmentContour.bottomPx || height) / height) * 100, 2, 98);
      return {
        left,
        right: Math.max(left + 2, right),
        top,
        bottom: Math.max(top + 2, bottom),
        centerLeft: clamp((Number(garmentContour.centerXpx || (width / 2)) / width) * 100, 2, 98),
        centerTop: clamp((Number(garmentContour.centerYpx || (height / 2)) / height) * 100, 2, 98),
      };
    }
    const points = (printNodes || [])
      .map((node) => {
        const nodeSide = resolveSide(node);
        if (nodeSide !== null) {
          if (view === "front" && Number(nodeSide) !== 0) return null;
          if (view === "back" && Number(nodeSide) !== 1) return null;
        }
        const item = node?.Item || {};
        const fallbackNode = LEGACY_PRINT_MAP[node?.key] || {};
        const topVal = item.top != null ? item.top : fallbackNode.top;
        const leftVal = item.left != null ? item.left : fallbackNode.left;
        const top = coordToPct(topVal, stageMetrics?.height || 0, stageMetrics?.baseSize || LEGACY_BASE_SIZE);
        const left = coordToPct(leftVal, stageMetrics?.width || 0, stageMetrics?.baseSize || LEGACY_BASE_SIZE);
        if (top === null || left === null) return null;
        return { top, left };
      })
      .filter(Boolean);
    if (!points.length) return fallback;

    const leftValues = points.map((p) => p.left);
    const topValues = points.map((p) => p.top);
    const minLeft = Math.min(...leftValues);
    const maxLeft = Math.max(...leftValues);
    const minTop = Math.min(...topValues);
    const maxTop = Math.max(...topValues);
    const sidePadding = 8;
    const verticalPadding = 10;

    const left = clamp(minLeft - sidePadding, 5, 95);
    const right = clamp(maxLeft + sidePadding, 5, 95);
    const top = clamp(minTop - verticalPadding, 4, 96);
    const bottom = clamp(maxTop + verticalPadding, 4, 96);

    return {
      left,
      right: Math.max(left + 2, right),
      top,
      bottom: Math.max(top + 2, bottom),
      centerLeft: (left + right) / 2,
      centerTop: (top + bottom) / 2,
    };
  }, [garmentContour, printNodes, view, stageMetrics]);

  const garmentMaskCanvas = useMemo(() => {
    const width = Math.max(1, Math.round(Number(stageSize.width || 0)));
    const height = Math.max(1, Math.round(Number(stageSize.height || 0)));
    if (width <= 1 || height <= 1 || typeof document === "undefined") return null;
    return buildGarmentMask({
      width,
      height,
      garmentContour,
      garmentBounds,
    });
  }, [stageSize.width, stageSize.height, garmentContour, garmentBounds]);

  useEffect(() => {
    const width = Math.max(1, Math.round(Number(stageSize.width || 0)));
    const height = Math.max(1, Math.round(Number(stageSize.height || 0)));
    let cancelled = false;
    if (width <= 1 || height <= 1 || !hasActiveCustomCuts) {
      setCustomCutRuntime(null);
      onCustomCutRuntimeUpdate?.(view, []);
      return undefined;
    }
    (async () => {
      const runtime = await buildCustomCutRuntime({
        layers,
        side: view,
        width,
        height,
        customCutsBySide,
        customCutZoneColors,
      });
      if (cancelled) return;
      setCustomCutRuntime(runtime);
      onCustomCutRuntimeUpdate?.(view, runtime?.zones || [], runtime);
    })();
    return () => {
      cancelled = true;
    };
  }, [stageSize.width, stageSize.height, layers, view, customCutsBySide, customCutZoneColors, onCustomCutRuntimeUpdate, hasActiveCustomCuts]);

  useEffect(() => {
    const width = Math.max(1, Math.round(Number(stageSize.width || 0)));
    const height = Math.max(1, Math.round(Number(stageSize.height || 0)));
    if (width <= 1 || height <= 1 || !garmentMaskCanvas || typeof document === "undefined") {
      setStripeSnapTargetMap(null);
      return undefined;
    }
    let cancelled = false;
    (async () => {
      try {
        const maskCtx = garmentMaskCanvas.getContext("2d");
        if (!maskCtx) {
          if (!cancelled) setStripeSnapTargetMap(null);
          return;
        }
        const maskData = maskCtx.getImageData(0, 0, width, height).data;
        const maskAlpha = new Uint8ClampedArray(width * height);
        for (let i = 0, p = 0; i < maskData.length; i += 4, p += 1) {
          maskAlpha[p] = maskData[i + 3];
        }
        const silhouettePoints = buildSilhouetteEdgePoints({ maskAlpha, width, height });

        const layerCanvas = document.createElement("canvas");
        layerCanvas.width = width;
        layerCanvas.height = height;
        const layerCtx = layerCanvas.getContext("2d");
        if (!layerCtx) {
          const alpha = buildSnapTargetAlpha(silhouettePoints, width, height);
          if (!cancelled) setStripeSnapTargetMap(alpha.length ? { width, height, alpha } : null);
          return;
        }
        const allInnerPoints = [];
        for (const raw of stripeSnapSourceLayers) {
          const src = getImgUrl(raw);
          if (!src) continue;
          const image = await loadCanvasImage(src).catch(() => null);
          if (!image || cancelled) continue;
          layerCtx.clearRect(0, 0, width, height);
          layerCtx.drawImage(
            image,
            0,
            0,
            image.naturalWidth || image.width || width,
            image.naturalHeight || image.height || height,
            0,
            0,
            width,
            height
          );
          const layerData = layerCtx.getImageData(0, 0, width, height).data;
          allInnerPoints.push(
            ...buildLayerAlphaEdgePoints({
              layerData,
              maskAlpha,
              width,
              height,
            }),
            ...buildInternalContourPoints({
              detailData: layerData,
              maskAlpha,
              width,
              height,
            })
          );
        }
        if (cancelled) return;
        const alpha = buildSnapTargetAlpha([...silhouettePoints, ...allInnerPoints], width, height);
        if (!cancelled) {
          setStripeSnapTargetMap(alpha.length ? { width, height, alpha } : null);
        }
      } catch (_error) {
        if (!cancelled) setStripeSnapTargetMap(null);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [stageSize.width, stageSize.height, garmentMaskCanvas, stripeSnapSourceLayers, view]);

  // Devuelve el canvas-máscara de la región donde cae un punto normalizado (por prioridad de región),
  // o null si no cae en ninguna → el que llama usa la silueta entera como fallback.
  const regionMaskForPoint = useCallback((nx, ny) => {
    const keys = Object.keys(regionMaskCanvases || {});
    if (!keys.length) return null;
    const ordered = [
      ...STRIPE_REGION_ORDER.filter((r) => keys.includes(r)),
      ...keys.filter((r) => !STRIPE_REGION_ORDER.includes(r)),
    ];
    for (const region of ordered) {
      const canvas = regionMaskCanvases[region];
      const ctx = canvas?.getContext?.("2d");
      if (!ctx) continue;
      const x = clamp(Math.round(Number(nx) * (canvas.width - 1)), 0, canvas.width - 1);
      const y = clamp(Math.round(Number(ny) * (canvas.height - 1)), 0, canvas.height - 1);
      try {
        if (ctx.getImageData(x, y, 1, 1).data[3] > 18) return canvas;
      } catch (_e) {
        // ignora
      }
    }
    return null;
  }, [regionMaskCanvases]);

  // z de la parte que queda MÁS ARRIBA en el punto (máximo z entre las partes que cubren ese
  // pixel). La tira que arranca ahí hereda ese z. null si no cae en ninguna parte.
  const partZForPoint = useCallback((nx, ny) => {
    const keys = Object.keys(zMaskCanvases || {});
    if (!keys.length) return null;
    let best = null;
    for (const key of keys) {
      const canvas = zMaskCanvases[key];
      const ctx = canvas?.getContext?.("2d");
      if (!ctx) continue;
      const x = clamp(Math.round(Number(nx) * (canvas.width - 1)), 0, canvas.width - 1);
      const y = clamp(Math.round(Number(ny) * (canvas.height - 1)), 0, canvas.height - 1);
      try {
        if (ctx.getImageData(x, y, 1, 1).data[3] > 18) {
          const z = Number(key);
          if (Number.isFinite(z)) best = best == null ? z : Math.max(best, z);
        }
      } catch (_e) {
        // ignora
      }
    }
    return best;
  }, [zMaskCanvases]);

  const commitStripeStroke = useCallback((points, tool = "line") => {
    if (!onUpdateStripeDrawingsBySide) return;
    if (!Array.isArray(points) || points.length < 2) return;
    const normalizedTool = tool === "line" ? "line" : "curve";
    const width = Math.max(1, Math.round(Number(stageSize.width || 0)));
    const height = Math.max(1, Math.round(Number(stageSize.height || 0)));
    const normalizedPoints = normalizedTool === "line"
      ? [points[0], points[points.length - 1]]
      : points;
    // La RECTA se recorta a la región donde arranca (no cruza a otra parte); la curva queda libre
    // (solo la silueta). Fallback a la silueta entera si no hay máscara de región.
    const startPt = normalizedPoints[0];
    const regionMask = (normalizedTool === "line" && Array.isArray(startPt))
      ? regionMaskForPoint(Number(startPt[0]), Number(startPt[1]))
      : null;
    // z de la parte donde arranca la tira (recta o curva) → la tira lo hereda y renderiza a ese z.
    const startZ = Array.isArray(startPt)
      ? partZForPoint(Number(startPt[0]), Number(startPt[1]))
      : null;
    const chunks = splitStrokeByMask({
      points: normalizedPoints,
      tool: normalizedTool,
      maskCanvas: regionMask || garmentMaskCanvas,
      width,
      height,
    });
    if (!chunks.length) return;
    const strokeBase = {
      color: String(stripeDrawEditor?.color || "#1d4ed8"),
      thickness: Number(stripeDrawEditor?.thickness || 8),
      opacity: clamp(Number(stripeDrawEditor?.opacity ?? 0.94), 0.88, 1),
      z: Number.isFinite(startZ) ? startZ : null,
    };
    const strokes = chunks.map((chunk) => {
      const id = `stripe-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
      const snapResult = autoConnectStrokeEndpoints({
        points: chunk.points_norm,
        tool: chunk.tool,
        thickness: strokeBase.thickness,
        snapTargetMap: stripeSnapTargetMap,
      });
      const correctedPoints = Array.isArray(snapResult?.points) ? snapResult.points : chunk.points_norm;
      const coverage = getStrokeCollisionCoverage(stripeSleeveCollisionMap, correctedPoints);
      const ratio = coverage.samples > 0 ? (coverage.hits / coverage.samples) : 0;
      const hasSleeveCollisionMap = !!stripeSleeveCollisionMap?.alpha?.length;
      const sleeveByCollision = hasSleeveCollisionMap && ratio >= 0.3;
      const sleeveByGeometry = !hasSleeveCollisionMap && isSleeveStrokeByGeometry({
        points: correctedPoints,
        garmentContour,
        width,
        height,
      });
      const mirrorScope = (sleeveByCollision || sleeveByGeometry) ? "sleeve" : "none";
      return {
        id,
        ...strokeBase,
        tool: chunk.tool,
        points_norm: correctedPoints,
        mirror_scope: mirrorScope,
        mirror_group_id: id,
        mirrored_from_side: null,
        snap_applied_start: !!snapResult?.snapAppliedStart,
        snap_applied_end: !!snapResult?.snapAppliedEnd,
        snap_target_start_norm: Array.isArray(snapResult?.startTargetNorm) ? snapResult.startTargetNorm : null,
        snap_target_end_norm: Array.isArray(snapResult?.endTargetNorm) ? snapResult.endTargetNorm : null,
      };
    });
    onUpdateStripeDrawingsBySide(view, (prev) => ([...(Array.isArray(prev) ? prev : []), ...strokes]));
    const latestStrokeId = String(strokes[strokes.length - 1]?.id || "");
    if (latestStrokeId) {
      setSelectedStrokeId(latestStrokeId);
    }
    onSetStripeDrawEditor?.((prev) => ({
      ...(prev || {}),
      enabled: prev?.tool && prev.tool !== "none",
    }));
  }, [
    onUpdateStripeDrawingsBySide,
    onSetStripeDrawEditor,
    stripeDrawEditor?.color,
    stripeDrawEditor?.thickness,
    stripeDrawEditor?.opacity,
    view,
    stageSize.width,
    stageSize.height,
    garmentContour,
    garmentMaskCanvas,
    stripeSnapTargetMap,
    stripeSleeveCollisionMap,
    regionMaskForPoint,
    partZForPoint,
  ]);

  const commitCustomCutStroke = useCallback((points, tool = "line") => {
    if (!onUpdateCustomCutsBySide) return;
    const width = Math.max(1, Math.round(Number(stageSize.width || 0)));
    const height = Math.max(1, Math.round(Number(stageSize.height || 0)));
    if (!Array.isArray(points) || points.length < 2 || width <= 1 || height <= 1) return;
    const normalizedTool = tool === "line" ? "line" : "curve";
    const snapResult = autoConnectStrokeEndpoints({
      points: normalizedTool === "line" ? [points[0], points[points.length - 1]] : points,
      tool: normalizedTool,
      thickness: 8,
      snapTargetMap: stripeSnapTargetMap,
    });
    const correctedPoints = Array.isArray(snapResult?.points) ? snapResult.points : points;
    const anchoredPoints = correctedPoints.map((point) => [clamp(Number(point?.[0] || 0), 0, 1), clamp(Number(point?.[1] || 0), 0, 1)]);
    if (Array.isArray(snapResult?.startTargetNorm) && snapResult.startTargetNorm.length >= 2 && anchoredPoints.length >= 1) {
      anchoredPoints[0] = [
        clamp(Number(snapResult.startTargetNorm[0] || 0), 0, 1),
        clamp(Number(snapResult.startTargetNorm[1] || 0), 0, 1),
      ];
    }
    if (Array.isArray(snapResult?.endTargetNorm) && snapResult.endTargetNorm.length >= 2 && anchoredPoints.length >= 2) {
      anchoredPoints[anchoredPoints.length - 1] = [
        clamp(Number(snapResult.endTargetNorm[0] || 0), 0, 1),
        clamp(Number(snapResult.endTargetNorm[1] || 0), 0, 1),
      ];
    }
    const chunks = splitStrokeByMask({
      points: anchoredPoints,
      tool: normalizedTool,
      maskCanvas: garmentMaskCanvas,
      width,
      height,
    });
    if (!chunks.length) return;
    const nextCuts = chunks
      .map((chunk) => {
        const stagePointsPx = (Array.isArray(chunk?.points_norm) ? chunk.points_norm : []).map((point) => [
          clamp(Number(point?.[0] || 0), 0, 1) * width,
          clamp(Number(point?.[1] || 0), 0, 1) * height,
        ]);
        return createRelativeCustomCutFromStagePoints({
          tool: chunk?.tool === "line" ? "line" : "curve",
          stagePoints: stagePointsPx,
          stageWidth: width,
          stageHeight: height,
          targetGroup: "free",
        });
      })
      .filter(Boolean);
    if (!nextCuts.length) return;
    onUpdateCustomCutsBySide(view, (prev) => ([...(Array.isArray(prev) ? prev : []), ...nextCuts]));
    setSelectedCustomCutId(String(nextCuts[nextCuts.length - 1]?.id || ""));
    onSetCustomCutEditor?.((prev) => ({
      ...(prev || {}),
      tool: "none",
      enabled: false,
    }));
  }, [
    onUpdateCustomCutsBySide,
    onSetCustomCutEditor,
    view,
    stageSize.width,
    stageSize.height,
    stripeSnapTargetMap,
    garmentMaskCanvas,
  ]);

  // Dibuja las tiras COMMITEADAS: un canvas por bucket de z (cada uno recortado a la silueta).
  // Así una tira que arranca en el frente queda a z bajo y una parte con z mayor (bolsillo) la tapa.
  useEffect(() => {
    const width = Math.max(1, Math.round(Number(stageSize.width || 0)));
    const height = Math.max(1, Math.round(Number(stageSize.height || 0)));
    if (width <= 1 || height <= 1) return undefined;
    const maskCanvas = garmentMaskCanvas || buildGarmentMask({
      width,
      height,
      garmentContour,
      garmentBounds,
    });
    stripeZLayers.forEach(({ z, strokes }) => {
      const canvas = stripeBucketCanvasRefs.current.get(z);
      if (!canvas) return;
      canvas.width = width;
      canvas.height = height;
      const ctx = canvas.getContext("2d");
      if (!ctx) return;
      ctx.clearRect(0, 0, width, height);
      (strokes || []).forEach((stroke) => {
        drawStripeStroke(ctx, stroke, width, height);
      });
      if (maskCanvas) {
        ctx.globalCompositeOperation = "destination-in";
        ctx.drawImage(maskCanvas, 0, 0, width, height);
        ctx.globalCompositeOperation = "source-over";
      }
      if (STRIPE_SNAP_DEBUG_VISUAL) {
        (strokes || []).forEach((stroke) => {
          drawStripeSnapDebug(ctx, stroke, width, height);
        });
      }
    });
    return undefined;
  }, [
    stageSize.width,
    stageSize.height,
    garmentMaskCanvas,
    garmentContour,
    garmentBounds,
    stripeZLayers,
  ]);

  // Dibuja el BORRADOR (trazo en curso) en su propio canvas, siempre por encima de todo.
  useEffect(() => {
    const canvas = stripeDraftCanvasRef.current;
    const width = Math.max(1, Math.round(Number(stageSize.width || 0)));
    const height = Math.max(1, Math.round(Number(stageSize.height || 0)));
    if (!canvas || width <= 1 || height <= 1) return undefined;
    canvas.width = width;
    canvas.height = height;
    const ctx = canvas.getContext("2d");
    if (!ctx) return undefined;
    ctx.clearRect(0, 0, width, height);
    if (draftStrokePoints.length < 2 || !stripeDrawEditor?.enabled) return undefined;
    drawStripeStroke(ctx, {
      tool: stripeDrawEditor?.tool === "line" ? "line" : "curve",
      color: stripeDrawEditor?.color || "#1d4ed8",
      thickness: Number(stripeDrawEditor?.thickness || 8),
      opacity: clamp(Number(stripeDrawEditor?.opacity ?? 0.94), 0.88, 1),
      points_norm: draftStrokePoints,
    }, width, height);
    const maskCanvas = garmentMaskCanvas || buildGarmentMask({
      width,
      height,
      garmentContour,
      garmentBounds,
    });
    if (maskCanvas) {
      ctx.globalCompositeOperation = "destination-in";
      ctx.drawImage(maskCanvas, 0, 0, width, height);
      ctx.globalCompositeOperation = "source-over";
    }
    if (STRIPE_SNAP_DEBUG_VISUAL) {
      const draftSnap = autoConnectStrokeEndpoints({
        points: draftStrokePoints,
        tool: stripeDrawEditor?.tool === "line" ? "line" : "curve",
        thickness: Number(stripeDrawEditor?.thickness || 8),
        snapTargetMap: stripeSnapTargetMap,
      });
      drawStripeSnapDebug(
        ctx,
        {
          thickness: Number(stripeDrawEditor?.thickness || 8),
          points_norm: draftStrokePoints,
          snap_applied_start: !!draftSnap?.snapAppliedStart,
          snap_applied_end: !!draftSnap?.snapAppliedEnd,
          snap_target_start_norm: Array.isArray(draftSnap?.startTargetNorm) ? draftSnap.startTargetNorm : null,
          snap_target_end_norm: Array.isArray(draftSnap?.endTargetNorm) ? draftSnap.endTargetNorm : null,
        },
        width,
        height
      );
    }
    return undefined;
  }, [
    stageSize.width,
    stageSize.height,
    garmentMaskCanvas,
    garmentContour,
    garmentBounds,
    draftStrokePoints,
    stripeSnapTargetMap,
    stripeDrawEditor,
  ]);

  useEffect(() => {
    const canvas = customCutCanvasRef.current;
    const width = Math.max(1, Math.round(Number(stageSize.width || 0)));
    const height = Math.max(1, Math.round(Number(stageSize.height || 0)));
    if (!canvas || width <= 1 || height <= 1) return undefined;
    canvas.width = width;
    canvas.height = height;
    const ctx = canvas.getContext("2d");
    if (!ctx) return undefined;
    ctx.clearRect(0, 0, width, height);
    if (customCutRuntime) {
      drawCustomCutRuntime({
        ctx,
        runtime: customCutRuntime,
        width,
        height,
        drawFills: false,
        drawLines: true,
      });
    }
    if (draftCustomCutPoints.length >= 2 && customCutEditor?.enabled) {
      const normalizedTool = customCutEditor?.tool === "line" ? "line" : "curve";
      const draftSnap = autoConnectStrokeEndpoints({
        points: normalizedTool === "line" ? [draftCustomCutPoints[0], draftCustomCutPoints[draftCustomCutPoints.length - 1]] : draftCustomCutPoints,
        tool: normalizedTool,
        thickness: 8,
        snapTargetMap: stripeSnapTargetMap,
      });
      const draftSegments = splitStrokeByMask({
        points: Array.isArray(draftSnap?.points) ? draftSnap.points : draftCustomCutPoints,
        tool: normalizedTool,
        maskCanvas: garmentMaskCanvas,
        width,
        height,
      });
      draftSegments.forEach((segment) => {
        drawCustomCutStroke(
          ctx,
          { points_norm: segment.points_norm, tool: segment.tool },
          width,
          height,
          {
            color: "rgba(230, 88, 197, 0.9)",
            lineWidth: 4,
            dashed: true,
          }
        );
      });
    }
    if (garmentMaskCanvas) {
      ctx.globalCompositeOperation = "destination-in";
      ctx.drawImage(garmentMaskCanvas, 0, 0, width, height);
      ctx.globalCompositeOperation = "source-over";
    }
    return undefined;
  }, [
    stageSize.width,
    stageSize.height,
    customCutRuntime,
    draftCustomCutPoints,
    customCutEditor,
    stripeSnapTargetMap,
    garmentMaskCanvas,
  ]);

  useEffect(() => {
    const canvas = customCutFxCanvasRef.current;
    const width = Math.max(1, Math.round(Number(stageSize.width || 0)));
    const height = Math.max(1, Math.round(Number(stageSize.height || 0)));
    if (!canvas || width <= 1 || height <= 1) return undefined;
    canvas.width = width;
    canvas.height = height;
    const ctx = canvas.getContext("2d");
    if (!ctx) return undefined;
    ctx.clearRect(0, 0, width, height);
    const highlightedZone = customCutRuntime?.zonesById?.[String(highlightedCustomCutZoneId || "")] || null;
    if (highlightedZone?.maskCanvas) {
      ctx.drawImage(highlightedZone.maskCanvas, 0, 0, width, height);
      ctx.globalCompositeOperation = "source-in";
      ctx.fillStyle = PART_HIGHLIGHT_FILL;
      ctx.fillRect(0, 0, width, height);
      ctx.globalCompositeOperation = "source-over";
    }
    if (customCutSparkleVisible && Array.isArray(sparkleCustomCutZoneIds) && sparkleCustomCutZoneIds.length) {
      sparkleCustomCutZoneIds.forEach((zoneId, index) => {
        const zone = customCutRuntime?.zonesById?.[String(zoneId)] || null;
        if (!zone?.alpha?.length) return;
        const validPoints = [];
        for (let y = Math.max(4, zone?.bbox?.minY || 0); y <= Math.min(height - 4, zone?.bbox?.maxY || height - 1); y += 6) {
          for (let x = Math.max(4, zone?.bbox?.minX || 0); x <= Math.min(width - 4, zone?.bbox?.maxX || width - 1); x += 6) {
            if (zone.alpha[(y * width) + x] > 0) validPoints.push([x, y]);
          }
        }
        const sparkleCount = Math.min(14, Math.max(8, Math.round(validPoints.length / 140)));
        for (let i = 0; i < sparkleCount; i += 1) {
          const chosen = validPoints[Math.floor(Math.random() * validPoints.length)];
          if (!chosen) continue;
          const [x, y] = chosen;
          const size = 6 + (((i + index) % 3) * 2);
          ctx.strokeStyle = "#e658c5";
          ctx.lineWidth = 1.6;
          ctx.beginPath();
          ctx.moveTo(x - size, y);
          ctx.lineTo(x + size, y);
          ctx.moveTo(x, y - size);
          ctx.lineTo(x, y + size);
          ctx.stroke();
          ctx.strokeStyle = "#ffffff";
          ctx.lineWidth = 1;
          ctx.beginPath();
          ctx.moveTo(x - (size * 0.55), y - (size * 0.55));
          ctx.lineTo(x + (size * 0.55), y + (size * 0.55));
          ctx.moveTo(x + (size * 0.55), y - (size * 0.55));
          ctx.lineTo(x - (size * 0.55), y + (size * 0.55));
          ctx.stroke();
        }
      });
    }
    return undefined;
  }, [
    stageSize.width,
    stageSize.height,
    customCutRuntime,
    highlightedCustomCutZoneId,
    sparkleCustomCutZoneIds,
    sparkleCustomCutToken,
    customCutSparkleVisible,
  ]);

  useEffect(() => {
    const width = Math.max(1, Math.round(Number(stageSize.width || 0)));
    const height = Math.max(1, Math.round(Number(stageSize.height || 0)));
    if (width <= 1 || height <= 1 || (!stripeCollisionLayers.length && !activeStripeStrokes.length) || !garmentMaskCanvas || typeof document === "undefined") {
      setStripeCollisionMap(null);
      return undefined;
    }
    let cancelled = false;
    (async () => {
      try {
        const canvas = document.createElement("canvas");
        canvas.width = width;
        canvas.height = height;
        const ctx = canvas.getContext("2d");
        if (!ctx) {
          if (!cancelled) setStripeCollisionMap(null);
          return;
        }
        ctx.clearRect(0, 0, width, height);
        for (const srcRaw of stripeCollisionLayers) {
          const src = getImgUrl(srcRaw);
          if (!src) continue;
          const image = await loadCanvasImage(src).catch(() => null);
          if (!image || cancelled) continue;
          ctx.drawImage(
            image,
            0,
            0,
            image.naturalWidth || image.width || width,
            image.naturalHeight || image.height || height,
            0,
            0,
            width,
            height
          );
        }
        (activeStripeStrokes || []).forEach((stroke) => {
          drawStripeStroke(ctx, stroke, width, height);
        });
        if (cancelled) return;
        ctx.globalCompositeOperation = "destination-in";
        ctx.drawImage(garmentMaskCanvas, 0, 0, width, height);
        ctx.globalCompositeOperation = "source-over";
        const data = ctx.getImageData(0, 0, width, height).data;
        const alpha = new Uint8ClampedArray(width * height);
        for (let i = 0, p = 0; i < data.length; i += 4, p += 1) {
          alpha[p] = data[i + 3];
        }
        if (!cancelled) setStripeCollisionMap({ width, height, alpha });
      } catch (_error) {
        if (!cancelled) setStripeCollisionMap(null);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [stageSize.width, stageSize.height, stripeCollisionLayers, activeStripeStrokes, garmentMaskCanvas]);

  useEffect(() => {
    const width = Math.max(1, Math.round(Number(stageSize.width || 0)));
    const height = Math.max(1, Math.round(Number(stageSize.height || 0)));
    if (width <= 1 || height <= 1 || !stripeSleeveCollisionLayers.length || !garmentMaskCanvas || typeof document === "undefined") {
      setStripeSleeveCollisionMap(null);
      return undefined;
    }
    let cancelled = false;
    (async () => {
      try {
        const canvas = document.createElement("canvas");
        canvas.width = width;
        canvas.height = height;
        const ctx = canvas.getContext("2d");
        if (!ctx) {
          if (!cancelled) setStripeSleeveCollisionMap(null);
          return;
        }
        ctx.clearRect(0, 0, width, height);
        for (const srcRaw of stripeSleeveCollisionLayers) {
          const src = getImgUrl(srcRaw);
          if (!src) continue;
          const image = await loadCanvasImage(src).catch(() => null);
          if (!image || cancelled) continue;
          ctx.drawImage(
            image,
            0,
            0,
            image.naturalWidth || image.width || width,
            image.naturalHeight || image.height || height,
            0,
            0,
            width,
            height
          );
        }
        if (cancelled) return;
        ctx.globalCompositeOperation = "destination-in";
        ctx.drawImage(garmentMaskCanvas, 0, 0, width, height);
        ctx.globalCompositeOperation = "source-over";
        const data = ctx.getImageData(0, 0, width, height).data;
        const alpha = new Uint8ClampedArray(width * height);
        for (let i = 0, p = 0; i < data.length; i += 4, p += 1) {
          alpha[p] = data[i + 3];
        }
        if (!cancelled) setStripeSleeveCollisionMap({ width, height, alpha });
      } catch (_error) {
        if (!cancelled) setStripeSleeveCollisionMap(null);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [stageSize.width, stageSize.height, stripeSleeveCollisionLayers, garmentMaskCanvas]);

  // Máscara-canvas por REGIÓN (unión de las partes de la región), para recortar la tira a la región
  // donde arranca (no cruza de una región a otra; sí cruza recortes dentro de la misma región).
  useEffect(() => {
    const width = Math.max(1, Math.round(Number(stageSize.width || 0)));
    const height = Math.max(1, Math.round(Number(stageSize.height || 0)));
    const regionKeys = Object.keys(regionLayerImages || {});
    if (width <= 1 || height <= 1 || !regionKeys.length || typeof document === "undefined") {
      setRegionMaskCanvases({});
      return undefined;
    }
    let cancelled = false;
    (async () => {
      const result = {};
      for (const region of regionKeys) {
        const canvas = document.createElement("canvas");
        canvas.width = width;
        canvas.height = height;
        const ctx = canvas.getContext("2d");
        if (!ctx) continue;
        for (const srcRaw of (regionLayerImages[region] || [])) {
          const src = getImgUrl(srcRaw);
          if (!src) continue;
          const image = await loadCanvasImage(src).catch(() => null);
          if (cancelled) return;
          if (!image) continue;
          ctx.drawImage(
            image,
            0, 0,
            image.naturalWidth || image.width || width,
            image.naturalHeight || image.height || height,
            0, 0, width, height
          );
        }
        result[region] = canvas;
      }
      if (!cancelled) setRegionMaskCanvases(result);
    })();
    return () => {
      cancelled = true;
    };
  }, [regionLayerImages, stageSize.width, stageSize.height]);

  // Máscara-canvas por Z (unión de las partes con ese z). Sirve para hit-testear qué parte queda
  // más arriba en el punto de arranque de la tira → la tira hereda ese z (apilado). Independiente
  // de la región: funciona aunque las regiones no estén clasificadas.
  useEffect(() => {
    const width = Math.max(1, Math.round(Number(stageSize.width || 0)));
    const height = Math.max(1, Math.round(Number(stageSize.height || 0)));
    const zKeys = Object.keys(zLayerImages || {});
    if (width <= 1 || height <= 1 || !zKeys.length || typeof document === "undefined") {
      setZMaskCanvases({});
      return undefined;
    }
    let cancelled = false;
    (async () => {
      const result = {};
      for (const zKey of zKeys) {
        const canvas = document.createElement("canvas");
        canvas.width = width;
        canvas.height = height;
        const ctx = canvas.getContext("2d");
        if (!ctx) continue;
        for (const srcRaw of (zLayerImages[zKey] || [])) {
          const src = getImgUrl(srcRaw);
          if (!src) continue;
          const image = await loadCanvasImage(src).catch(() => null);
          if (cancelled) return;
          if (!image) continue;
          ctx.drawImage(
            image,
            0, 0,
            image.naturalWidth || image.width || width,
            image.naturalHeight || image.height || height,
            0, 0, width, height
          );
        }
        result[zKey] = canvas;
      }
      if (!cancelled) setZMaskCanvases(result);
    })();
    return () => {
      cancelled = true;
    };
  }, [zLayerImages, stageSize.width, stageSize.height]);


  const handleStripeCollisionWarning = useCallback(() => {
    const now = Date.now();
    if ((now - stripeWarningCooldownRef.current) < 700) return;
    stripeWarningCooldownRef.current = now;
    setStripeWarningVisible(true);
    if (stripeWarningTimeoutRef.current) {
      clearTimeout(stripeWarningTimeoutRef.current);
    }
    stripeWarningTimeoutRef.current = setTimeout(() => {
      setStripeWarningVisible(false);
      stripeWarningTimeoutRef.current = null;
    }, 1700);
  }, []);

  const printClipCooldownRef = useRef(0);
  const handlePrintClipWarning = useCallback(() => {
    const now = Date.now();
    if ((now - printClipCooldownRef.current) < 1200) return;
    printClipCooldownRef.current = now;
    onPrintClipWarning?.();
  }, [onPrintClipWarning]);

  useEffect(() => () => {
    if (stripeWarningTimeoutRef.current) clearTimeout(stripeWarningTimeoutRef.current);
  }, []);

  const visibleHotspots = useMemo(
    () => {
      if (!showHotspots) return [];
      return (partHotspots || []).filter((spot) => {
        if (!spot) return false;
        if (spot.side && spot.side !== view) return false;
        return (
          coordToPct(spot.top, stageMetrics.height, stageMetrics.baseSize) !== null
          && coordToPct(spot.left, stageMetrics.width, stageMetrics.baseSize) !== null
        );
      });
    },
    [partHotspots, view, stageMetrics, showHotspots]
  );

  const handleStagePartTap = useCallback(async (event) => {
    if (!enablePartPaint) return;
    if (stripeDrawEditor?.enabled) return;
    if (customCutEditor?.enabled) return;
    const stageElement = stageRef.current;
    if (!stageElement) return;
    const rect = stageElement.getBoundingClientRect();
    if (!rect.width || !rect.height) return;
    const localX = event.clientX - rect.left;
    const localY = event.clientY - rect.top;
    const runtimeZones = Array.isArray(customCutRuntime?.zones) ? customCutRuntime.zones : [];
    if (runtimeZones.length && typeof onPreviewPaintCustomCutZone === "function") {
      const width = Math.max(1, Math.round(rect.width));
      const height = Math.max(1, Math.round(rect.height));
      const x = clamp(Math.round(localX), 0, width - 1);
      const y = clamp(Math.round(localY), 0, height - 1);
      for (let index = runtimeZones.length - 1; index >= 0; index -= 1) {
        const zone = runtimeZones[index];
        if (!zone?.alpha?.length) continue;
        if (zone.alpha[(y * width) + x] <= 0) continue;
        event.preventDefault();
        event.stopPropagation();
        onPreviewPaintCustomCutZone({
          zoneId: String(zone.id),
          zoneLabel: zone.label,
          targetGroup: zone.targetGroup,
          selectedColor: zone.color || null,
        });
        return;
      }
    }
    if (typeof onSelectPart !== "function") return;
    const maskLayers = layers.filter((layer) => layer.kind === "mask" && layer.partKey);
    if (!maskLayers.length) return;
    const width = Math.max(1, Math.round(rect.width));
    const height = Math.max(1, Math.round(rect.height));
    for (let index = maskLayers.length - 1; index >= 0; index -= 1) {
      const layer = maskLayers[index];
      const src = getImgUrl(layer?.img);
      if (!src) continue;
      const hitMap = await getCanvasMaskHitMap(src, width, height);
      if (!hitMap?.data?.length) continue;
      const x = clamp(Math.round(localX), 0, width - 1);
      const y = clamp(Math.round(localY), 0, height - 1);
      const alpha = hitMap.data[((y * width) + x) * 4 + 3];
      if (alpha <= 14) continue;
      event.preventDefault();
      event.stopPropagation();
      onSelectPart({
        partKey: String(layer.partKey),
        partLabel: sanitizePrintLocationLabel(layer.partLabel || "", "Parte"),
      });
      return;
    }
  }, [enablePartPaint, stripeDrawEditor?.enabled, customCutEditor?.enabled, onSelectPart, layers, customCutRuntime, onPreviewPaintCustomCutZone]);

  const releaseCustomCutGesture = useCallback((event) => {
    if (typeof event?.currentTarget?.releasePointerCapture === "function" && event?.pointerId != null) {
      try {
        event.currentTarget.releasePointerCapture(event.pointerId);
      } catch (_error) {
        // no-op
      }
    }
  }, []);

  const updateCustomCutCursorFromEvent = useCallback((event) => {
    const point = resolveNormalizedPoint(event.clientX, event.clientY);
    if (!point) {
      setCustomCutCursorPos(null);
      return null;
    }
    setCustomCutCursorPos(point);
    return point;
  }, [resolveNormalizedPoint]);

  return (
    <section className={styles.previewShell} aria-label="Vista previa de la prenda">
      <div className={styles.previewCard}>
        <div className={styles.previewStage}>
          <div
            ref={stageRef}
            className={styles.previewStageInner}
            onClick={(event) => {
              if (!enablePartPaint) return;
              if (stripeDrawEditor?.enabled) return;
              if (customCutEditor?.enabled) return;
              setPaintTipVisible(false);
              handleStagePartTap(event);
            }}
          >
          {layers.filter((layer) => layer.kind === "mask").map((l, idx) => (
            <PreviewLayer
              key={`mask-${idx}`}
              img={l.img}
              colorHex={l.colorHex}
              patternUrl={l.patternUrl}
              zIndex={l.zIndex}
              item={l.item}
              forceSolidFill={!!l.forceSolidFill}
              highlightOverlay={String(highlightedPartKey || "") === String(l.partKey || "")}
              sparkleActive={Array.isArray(sparklePartKeys) && sparklePartKeys.some((key) => String(key) === String(l.partKey || ""))}
              sparkleToken={sparkleToken}
              customCutZoneOverlays={activeCustomCutZoneOverlaysByPart[String(l.partKey || "")] || []}
            />
          ))}
          <canvas
            ref={customCutCanvasRef}
            style={{
              position: "absolute",
              inset: 0,
              width: "100%",
              height: "100%",
              pointerEvents: "none",
              zIndex: stripeLayerZ,
            }}
          />
          <canvas
            ref={customCutFxCanvasRef}
            style={{
              position: "absolute",
              inset: 0,
              width: "100%",
              height: "100%",
              pointerEvents: "none",
              zIndex: stripeLayerZ + 1,
            }}
          />
          {stripeZLayers.map(({ z }) => (
            <canvas
              key={`stripe-z-${z}`}
              ref={(el) => {
                const map = stripeBucketCanvasRefs.current;
                if (el) map.set(z, el);
                else map.delete(z);
              }}
              style={{
                position: "absolute",
                inset: 0,
                width: "100%",
                height: "100%",
                pointerEvents: "none",
                zIndex: z,
              }}
            />
          ))}
          <canvas
            ref={stripeDraftCanvasRef}
            style={{
              position: "absolute",
              inset: 0,
              width: "100%",
              height: "100%",
              pointerEvents: "none",
              zIndex: stripeLayerZ + 2,
            }}
          />
          {layers.filter((layer) => layer.kind === "detail").map((l, idx) => (
            <PreviewLayer
              key={`detail-${idx}`}
              img={l.img}
              colorHex={l.colorHex}
              patternUrl={l.patternUrl}
              zIndex={l.zIndex}
              item={l.item}
              forceSolidFill={!!l.forceSolidFill}
            />
          ))}
          {stripeDrawEditor?.enabled ? (
            <div
              style={{
                position: "absolute",
                inset: 0,
                zIndex: maxZ + 30,
                cursor: stripeDrawEditor?.tool === "erase"
                  ? "not-allowed"
                  : (stripeDrawEditor?.tool === "move" ? "grab" : "crosshair"),
                pointerEvents: "auto",
                touchAction: "none",
                overscrollBehavior: "contain",
                userSelect: "none",
                WebkitUserSelect: "none",
              }}
              onPointerDown={(event) => {
                const point = resolveNormalizedPoint(event.clientX, event.clientY);
                if (!point) return;
                event.preventDefault();
                event.stopPropagation();
                if (stripeDrawEditor?.tool === "move") {
                  const nearest = findNearestStrokeAtPoint(activeStripeStrokes, point);
                  if (!nearest?.stroke) {
                    setSelectedStrokeId(null);
                    setSelectedStrokeAnchor(null);
                    return;
                  }
                  setSelectedStrokeId(String(nearest.stroke.id || ""));
                  moveStripeRef.current = {
                    active: true,
                    strokeId: String(nearest.stroke.id || ""),
                    pointerId: event.pointerId,
                    lastPoint: point,
                  };
                  if (typeof event.currentTarget.setPointerCapture === "function") {
                    event.currentTarget.setPointerCapture(event.pointerId);
                  }
                  return;
                }
                if (stripeDrawEditor?.tool === "erase") {
                  if (!onUpdateStripeDrawingsBySide) return;
                  const nearest = findNearestStrokeAtPoint(activeStripeStrokes, point);
                  if (!nearest?.stroke) return;
                  const strokeId = String(nearest.stroke.id || "");
                  onUpdateStripeDrawingsBySide(view, (prev) => {
                    const list = Array.isArray(prev) ? prev : [];
                    return list.filter((stroke) => String(stroke?.id || "") !== strokeId);
                  });
                  if (selectedStrokeId && String(selectedStrokeId) === strokeId) {
                    setSelectedStrokeId(null);
                    setSelectedStrokeAnchor(null);
                  }
                  return;
                }
                setSelectedStrokeId(null);
                setSelectedStrokeAnchor(null);
                drawingRef.current = true;
                setDraftStrokePoints([point]);
              }}
              onPointerMove={(event) => {
                if (stripeDrawEditor?.tool === "move") {
                  const moveState = moveStripeRef.current;
                  if (!moveState.active || moveState.pointerId !== event.pointerId) return;
                  const point = resolveNormalizedPoint(event.clientX, event.clientY);
                  if (!point || !Array.isArray(moveState.lastPoint)) return;
                  const dx = point[0] - moveState.lastPoint[0];
                  const dy = point[1] - moveState.lastPoint[1];
                  moveState.lastPoint = point;
                  if (!onUpdateStripeDrawingsBySide || !moveState.strokeId) return;
                  onUpdateStripeDrawingsBySide(view, (prev) => {
                    const list = Array.isArray(prev) ? prev : [];
                    return list.map((stroke) => {
                      if (String(stroke?.id || "") !== String(moveState.strokeId)) return stroke;
                      const pts = Array.isArray(stroke?.points_norm) ? stroke.points_norm : [];
                      const moved = pts.map((p) => [
                        clamp(Number(p?.[0] || 0) + dx, 0, 1),
                        clamp(Number(p?.[1] || 0) + dy, 0, 1),
                      ]);
                      return { ...stroke, points_norm: moved };
                    });
                  });
                  event.preventDefault();
                  event.stopPropagation();
                  return;
                }
                if (!drawingRef.current) return;
                const point = resolveNormalizedPoint(event.clientX, event.clientY);
                if (!point) return;
                event.preventDefault();
                event.stopPropagation();
                setDraftStrokePoints((prev) => {
                  if (!prev.length) return [point];
                  if (stripeDrawEditor?.tool === "line") return [prev[0], point];
                  return [...prev, point];
                });
              }}
              onPointerUp={(event) => {
                if (stripeDrawEditor?.tool === "move") {
                  const moveState = moveStripeRef.current;
                  if (moveState.pointerId === event.pointerId) {
                    moveStripeRef.current = {
                      active: false,
                      strokeId: moveState.strokeId,
                      pointerId: null,
                      lastPoint: null,
                    };
                  }
                  return;
                }
                if (!drawingRef.current) return;
                event.preventDefault();
                event.stopPropagation();
                drawingRef.current = false;
                const points = draftStrokePointsRef.current;
                if (points.length >= 2) {
                  commitStripeStroke(points, stripeDrawEditor?.tool || "line");
                }
                setDraftStrokePoints([]);
              }}
              onPointerCancel={(event) => {
                if (stripeDrawEditor?.tool === "move") {
                  const moveState = moveStripeRef.current;
                  if (moveState.pointerId === event.pointerId) {
                    moveStripeRef.current = {
                      active: false,
                      strokeId: moveState.strokeId,
                      pointerId: null,
                      lastPoint: null,
                    };
                  }
                  return;
                }
                if (!drawingRef.current) return;
                drawingRef.current = false;
                const points = draftStrokePointsRef.current;
                if (points.length >= 2) {
                  commitStripeStroke(points, stripeDrawEditor?.tool || "line");
                }
                setDraftStrokePoints([]);
              }}
              onPointerLeave={() => {
                if (stripeDrawEditor?.tool === "move") {
                  const moveState = moveStripeRef.current;
                  if (moveState.active) {
                    moveStripeRef.current = {
                      active: false,
                      strokeId: moveState.strokeId,
                      pointerId: null,
                      lastPoint: null,
                    };
                  }
                  return;
                }
                if (!drawingRef.current) return;
                drawingRef.current = false;
                const points = draftStrokePointsRef.current;
                if (points.length >= 2) {
                  commitStripeStroke(points, stripeDrawEditor?.tool || "line");
                }
                setDraftStrokePoints([]);
              }}
            />
          ) : null}
          {!stripeDrawEditor?.enabled && customCutEditor?.enabled ? (
            <div
              style={{
                position: "absolute",
                inset: 0,
                zIndex: maxZ + 28,
                cursor: customCutEditor?.tool === "erase"
                  ? "not-allowed"
                  : (customCutEditor?.tool === "move" ? "grab" : "crosshair"),
                pointerEvents: "auto",
                touchAction: "none",
                overscrollBehavior: "contain",
                userSelect: "none",
                WebkitUserSelect: "none",
              }}
              onPointerDown={(event) => {
                const point = updateCustomCutCursorFromEvent(event);
                if (!point) return;
                event.preventDefault();
                event.stopPropagation();
                if (customCutEditor?.tool === "move") {
                  const nearest = findNearestCustomCutAtPoint(customCutRuntime?.renderedCuts || [], point);
                  if (!nearest?.cut) {
                    setSelectedCustomCutId(null);
                    setSelectedCustomCutAnchor(null);
                    return;
                  }
                  setSelectedCustomCutId(String(nearest.cut.id || ""));
                  moveCustomCutRef.current = {
                    active: true,
                    cutId: String(nearest.cut.id || ""),
                    pointerId: event.pointerId,
                    lastPoint: point,
                  };
                  if (typeof event.currentTarget.setPointerCapture === "function") {
                    event.currentTarget.setPointerCapture(event.pointerId);
                  }
                  return;
                }
                if (customCutEditor?.tool === "erase") {
                  const nearest = findNearestCustomCutAtPoint(customCutRuntime?.renderedCuts || [], point);
                  if (!nearest?.cut || !onUpdateCustomCutsBySide) return;
                  const cutId = String(nearest.cut.id || "");
                  onUpdateCustomCutsBySide(view, (prev) => {
                    const list = Array.isArray(prev) ? prev : [];
                    return list.filter((cut) => String(cut?.id || "") !== cutId);
                  });
                  if (String(selectedCustomCutId || "") === cutId) {
                    setSelectedCustomCutId(null);
                    setSelectedCustomCutAnchor(null);
                  }
                  return;
                }
                setSelectedCustomCutId(null);
                setSelectedCustomCutAnchor(null);
                drawingCustomCutRef.current = true;
                if (typeof event.currentTarget.setPointerCapture === "function") {
                  event.currentTarget.setPointerCapture(event.pointerId);
                }
                setDraftCustomCutPoints([point]);
              }}
              onPointerMove={(event) => {
                const point = updateCustomCutCursorFromEvent(event);
                if (customCutEditor?.tool === "move") {
                  const moveState = moveCustomCutRef.current;
                  if (!moveState.active || moveState.pointerId !== event.pointerId) return;
                  if (!point || !Array.isArray(moveState.lastPoint)) return;
                  const dxPx = (point[0] - moveState.lastPoint[0]) * Math.max(1, stageSize.width || 1);
                  const dyPx = (point[1] - moveState.lastPoint[1]) * Math.max(1, stageSize.height || 1);
                  moveState.lastPoint = point;
                  if (!onUpdateCustomCutsBySide || !moveState.cutId) return;
                  const activeCut = activeCustomCuts.find((cut) => String(cut?.id || "") === String(moveState.cutId));
                  if (!activeCut) return;
                  const component = resolveCustomCutRuntimeComponent({
                    runtime: customCutRuntime,
                    targetGroup: activeCut.target_group,
                    preferredSlot: activeCut.anchor_component,
                  });
                  if (!component) return;
                  onUpdateCustomCutsBySide(view, (prev) => {
                    const list = Array.isArray(prev) ? prev : [];
                    return list.map((cut) => {
                      if (String(cut?.id || "") !== String(moveState.cutId)) return cut;
                      return translateRelativeCustomCut({
                        cut,
                        deltaX: dxPx,
                        deltaY: dyPx,
                        component,
                        stageWidth: Math.max(1, stageSize.width || 1),
                        stageHeight: Math.max(1, stageSize.height || 1),
                      });
                    });
                  });
                  event.preventDefault();
                  event.stopPropagation();
                  return;
                }
                if (!drawingCustomCutRef.current || !point) return;
                event.preventDefault();
                event.stopPropagation();
                setDraftCustomCutPoints((prev) => {
                  if (!prev.length) return [point];
                  if (customCutEditor?.tool === "line") return [prev[0], point];
                  return [...prev, point];
                });
              }}
              onPointerUp={(event) => {
                if (customCutEditor?.tool === "move") {
                  const moveState = moveCustomCutRef.current;
                  if (moveState.pointerId === event.pointerId) {
                    moveCustomCutRef.current = {
                      active: false,
                      cutId: moveState.cutId,
                      pointerId: null,
                      lastPoint: null,
                    };
                  }
                  releaseCustomCutGesture(event);
                  return;
                }
                if (!drawingCustomCutRef.current) {
                  releaseCustomCutGesture(event);
                  return;
                }
                event.preventDefault();
                event.stopPropagation();
                drawingCustomCutRef.current = false;
                const points = draftCustomCutPointsRef.current;
                if (points.length >= 2) {
                  commitCustomCutStroke(points, customCutEditor?.tool || "line");
                }
                setDraftCustomCutPoints([]);
                releaseCustomCutGesture(event);
              }}
              onPointerCancel={(event) => {
                if (!drawingCustomCutRef.current && !moveCustomCutRef.current.active) {
                  releaseCustomCutGesture(event);
                  return;
                }
                drawingCustomCutRef.current = false;
                const points = draftCustomCutPointsRef.current;
                if (points.length >= 2) {
                  commitCustomCutStroke(points, customCutEditor?.tool || "line");
                }
                moveCustomCutRef.current = {
                  active: false,
                  cutId: null,
                  pointerId: null,
                  lastPoint: null,
                };
                setDraftCustomCutPoints([]);
                releaseCustomCutGesture(event);
              }}
              onPointerLeave={(event) => {
                setCustomCutCursorPos(null);
                if (!drawingCustomCutRef.current && !moveCustomCutRef.current.active) return;
                drawingCustomCutRef.current = false;
                const points = draftCustomCutPointsRef.current;
                if (points.length >= 2) {
                  commitCustomCutStroke(points, customCutEditor?.tool || "line");
                }
                moveCustomCutRef.current = {
                  active: false,
                  cutId: null,
                  pointerId: null,
                  lastPoint: null,
                };
                setDraftCustomCutPoints([]);
                releaseCustomCutGesture(event);
              }}
              onPointerEnter={(event) => {
                updateCustomCutCursorFromEvent(event);
              }}
            />
          ) : null}
          {!stripeDrawEditor?.enabled && customCutEditor?.enabled && (customCutEditor?.tool === "line" || customCutEditor?.tool === "curve") && customCutCursorPos ? (
            <div
              aria-hidden="true"
              style={{
                position: "absolute",
                left: `${customCutCursorPos[0] * 100}%`,
                top: `${customCutCursorPos[1] * 100}%`,
                transform: "translate(-40%, -82%) rotate(-12deg)",
                zIndex: maxZ + 29,
                pointerEvents: "none",
                width: 36,
                height: 36,
                borderRadius: 999,
                display: "grid",
                placeItems: "center",
                background: "rgba(230, 88, 197, 0.14)",
                border: "1px solid rgba(230, 88, 197, 0.42)",
                boxShadow: "0 10px 22px rgba(230, 88, 197, 0.18)",
                color: "#e658c5",
                backdropFilter: "blur(6px)",
              }}
            >
              <i
                className="bi bi-scissors"
                style={{
                  fontSize: 18,
                  lineHeight: 1,
                }}
              />
            </div>
          ) : null}
          {enableStripeEditing
            && selectedStrokeAnchor
            && (!stripeDrawEditor?.enabled || stripeDrawEditor?.tool === "none") ? (
            <div
              // Backdrop para cerrar el popover al tocar fuera (queda debajo de badges y popover).
              style={{
                position: "absolute",
                inset: 0,
                zIndex: maxZ + 18,
                background: "transparent",
                pointerEvents: "auto",
                touchAction: "manipulation",
              }}
              onPointerDown={(event) => {
                event.stopPropagation();
                setSelectedStrokeId(null);
                setSelectedStrokeAnchor(null);
              }}
            />
          ) : null}
          {enableStripeEditing && draftStrokePoints.length === 0
            ? stripeBadges.map((badge) => (
                String(badge.id) === String(selectedStrokeId) ? null : (
                  <button
                    key={`stripe-badge-${badge.id}`}
                    type="button"
                    className={styles.stripeBadge}
                    aria-label={`Editar tira ${badge.label}`}
                    title={`Editar ${badge.label}`}
                    style={{
                      left: `${clamp(badge.cx * 100, 3, 97)}%`,
                      top: `${clamp(badge.cy * 100, 3, 97)}%`,
                      zIndex: maxZ + 19,
                    }}
                    onPointerDown={(event) => {
                      event.stopPropagation();
                      event.preventDefault();
                      setSelectedStrokeId(String(badge.id));
                      setSelectedStrokeAnchor([badge.cx, badge.cy]);
                    }}
                  >
                    {badge.label}
                  </button>
                )
              ))
            : null}
          {enableStripeEditing && (!stripeDrawEditor?.enabled || stripeDrawEditor?.tool === "none") && selectedStrokeAnchor ? (
            <div
              className={styles.stripeEditPopover}
              style={{
                ...stripePopoverPlacement(selectedStrokeAnchor),
                maxWidth: "min(88vw, 340px)",
                flexWrap: "wrap",
                zIndex: maxZ + 20,
              }}
              onPointerDown={(event) => event.stopPropagation()}
            >
              <div className={`${styles.stripeEditColors} ${stripeEditColorsExpanded ? styles.stripeEditColorsExpanded : ""}`}>
                {(stripeColors || []).slice(0, stripeEditColorsExpanded ? (stripeColors?.length || 0) : 8).map((c) => {
                  const code = c.code || c.valor || "#1d4ed8";
                  return (
                    <button
                      key={`stripe-edit-color-${c.id ?? code}`}
                      type="button"
                      className={styles.stripeEditColorDot}
                      style={{ background: code }}
                      aria-label={c.name || c.nname || "Color"}
                      title={c.name || c.nname || "Color"}
                      onPointerDown={(event) => {
                        event.stopPropagation();
                        event.preventDefault();
                        const id = String(selectedStrokeId || "");
                        if (!id || !onUpdateStripeDrawingsBySide) return;
                        onUpdateStripeDrawingsBySide(view, (prev) => {
                          const list = Array.isArray(prev) ? prev : [];
                          return list.map((st) => (String(st?.id || "") === id ? { ...st, color: code } : st));
                        });
                      }}
                    />
                  );
                })}
                {!stripeEditColorsExpanded && (stripeColors || []).length > 8 ? (
                  <button
                    type="button"
                    className={styles.stripeEditMoreColorsBtn}
                    aria-label="Ver más colores"
                    title="Ver más colores"
                    onPointerDown={(event) => {
                      event.stopPropagation();
                      event.preventDefault();
                      setStripeEditColorsExpanded(true);
                    }}
                  >
                    <i className="bi bi-plus-lg" aria-hidden="true" />
                  </button>
                ) : null}
              </div>
              <button
                type="button"
                className={styles.stripeEditDeleteBtn}
                aria-label="Eliminar tira seleccionada"
                title="Eliminar tira"
                onPointerDown={(event) => {
                  event.stopPropagation();
                  event.preventDefault();
                  const idToRemove = String(selectedStrokeId || "");
                  if (!idToRemove || !onUpdateStripeDrawingsBySide) return;
                  onUpdateStripeDrawingsBySide(view, (prev) => {
                    const list = Array.isArray(prev) ? prev : [];
                    return list.filter((stroke) => String(stroke?.id || "") !== idToRemove);
                  });
                  setSelectedStrokeId(null);
                  setSelectedStrokeAnchor(null);
                }}
              >
                <i className="bi bi-trash3-fill" aria-hidden="true" />
              </button>
            </div>
          ) : null}
          {(!stripeDrawEditor?.enabled && !customCutEditor?.enabled) && selectedCustomCutAnchor ? (
            <button
              type="button"
              className={styles.stripeDeleteFloatingBtn}
              aria-label="Eliminar recorte seleccionado"
              title="Eliminar recorte seleccionado"
              style={{
                left: `${clamp(selectedCustomCutAnchor[0] * 100, 4, 96)}%`,
                top: `${clamp((selectedCustomCutAnchor[1] * 100) - 7, 4, 96)}%`,
              }}
              onPointerDown={(event) => {
                event.stopPropagation();
                event.preventDefault();
                const idToRemove = String(selectedCustomCutId || "");
                if (!idToRemove || !onUpdateCustomCutsBySide) return;
                onUpdateCustomCutsBySide(view, (prev) => {
                  const list = Array.isArray(prev) ? prev : [];
                  return list.filter((cut) => String(cut?.id || "") !== idToRemove);
                });
                setSelectedCustomCutId(null);
                setSelectedCustomCutAnchor(null);
              }}
            >
              <i className="bi bi-scissors" aria-hidden="true" />
            </button>
          ) : null}
          {visibleHotspots.map((spot) => {
            const top = coordToPct(spot.top, stageMetrics.height, stageMetrics.baseSize);
            const left = coordToPct(spot.left, stageMetrics.width, stageMetrics.baseSize);
            if (top === null || left === null) return null;
            const isActive = typeof spot.selected === "boolean"
              ? spot.selected
              : String(activePrintLocationKey) === String(spot.key);
            const displayLabel = String(spot.displayLabel || "Diseño");
            const tooltip = spot.partLabel
              ? `${spot.partLabel} · ${spot.label || "Ubicación de diseño"}`
              : (spot.label || "Ubicación de diseño");
            return (
              <button
                key={`hotspot-${spot.key}`}
                type="button"
                onClick={(event) => {
                  event.preventDefault();
                  event.stopPropagation();
                  onSelectPart?.(spot);
                }}
                title={tooltip}
                style={{
                  position: "absolute",
                  top: `${top}%`,
                  left: `${left}%`,
                  transform: "translate(-50%, -50%)",
                  zIndex: maxZ + 12,
                  border: "0",
                  borderRadius: 999,
                  minWidth: 56,
                  height: 56,
                  padding: "0 10px",
                  display: "grid",
                  placeItems: "center",
                  fontSize: displayLabel.length > 3 ? 11 : 16,
                  fontWeight: 700,
                  color: "#fff",
                  background: isActive ? "#76a3ea" : "#2f6ec8",
                  borderColor: "#ffffff",
                  borderStyle: "solid",
                  borderWidth: 3,
                  boxShadow: isActive
                    ? "0 10px 18px rgba(46, 86, 153, 0.38)"
                    : "0 8px 14px rgba(20, 36, 67, 0.32)",
                  cursor: "pointer",
                  lineHeight: 1,
                }}
              >
                {displayLabel}
              </button>
            );
          })}
          {showPrints && printNodes.map((n) => {
            const selectionsForLocation = Object.entries(selectedPrints || {})
              .filter(([printKey, sel]) => {
                const locationKey = String(sel?.attrs?.location_key || printKey).split("::")[0];
                return locationKey === String(n.key);
              })
              .sort((a, b) => comparePrintStackOrder(a, b, activePrintKey));
            if (!selectionsForLocation.length) return null;

            return selectionsForLocation.map(([printKey, selRaw]) => {
              let sel = selRaw;
              if (selRaw) {
                const orderedColorIds = ["color1", "color2", "color3"]
                  .map((key) => selRaw?.attrs?.[key])
                  .filter(Boolean);
                if (!orderedColorIds.length && selRaw?.attrs?.color) {
                  orderedColorIds.push(selRaw.attrs.color);
                }
                const uniqueOrderedColorIds = [];
                const seenColorIds = new Set();
                orderedColorIds.forEach((id) => {
                  const normalized = String(id);
                  if (!normalized || seenColorIds.has(normalized)) return;
                  seenColorIds.add(normalized);
                  uniqueOrderedColorIds.push(normalized);
                });
                const hexes = uniqueOrderedColorIds
                  .map((id) => allColors.find((c) => String(c.id) === String(id))?.code)
                  .filter(Boolean);
                if (hexes.length) {
                  sel = {
                    ...selRaw,
                    attrs: {
                      ...(selRaw.attrs || {}),
                      color_hexes: hexes,
                      color_hex: hexes[0],
                    },
                  };
                }
              }
              const selSide = sel?.attrs?.side;
              if (selSide !== undefined && selSide !== null) {
                if ((view === "back" && Number(selSide) !== 1) || (view === "front" && Number(selSide) === 1)) {
                  return null;
                }
              } else {
                const defaultSide = resolveSide(n);
                if ((view === "back" && Number(defaultSide) !== 1) || (view === "front" && Number(defaultSide) === 1)) {
                  return null;
                }
              }
              return (
                <PrintOverlay
                  key={`print-${String(printKey)}`}
                  printKey={String(printKey)}
                  node={n}
                  selection={sel}
                  boostZ={maxZ + 20}
                  active={enablePrintEditing && String(activePrintKey) === String(printKey)}
                  onSelect={enablePrintEditing ? onSelectPrint : null}
                  onTransform={enablePrintEditing ? onTransformPrint : null}
                  allColors={allColors}
                  onDeletePrint={enablePrintEditing ? onDeletePrint : null}
                  onEditColor={enablePrintEditing ? onEditPrintColor : null}
                  stageMetrics={stageMetrics}
                  garmentBounds={garmentBounds}
                  garmentContour={garmentContour}
                  garmentMaskCanvas={garmentMaskCanvas}
                  stripeCollisionMap={stripeCollisionMap}
                  onStripeCollision={handleStripeCollisionWarning}
                  onClipByMask={handlePrintClipWarning}
                  interactive={enablePrintEditing}
                />
              );
            });
          })}
          </div>
          {stripeWarningVisible ? (
            <div className={styles.previewInlineWarning}>
              No podés ubicar diseños sobre tiras.
            </div>
          ) : null}
          {enablePartPaint && paintTipVisible ? (
            <div
              style={{
                position: "absolute",
                top: 56,
                left: 14,
                zIndex: 2147483000,
                display: "inline-flex",
                alignItems: "center",
                gap: 8,
                padding: "8px 12px",
                borderRadius: 999,
                background: "rgba(255,255,255,0.96)",
                border: "1px solid #cdd9ea",
                boxShadow: "0 4px 14px rgba(16,35,63,0.14)",
                color: "#1e3a5f",
                fontSize: 13,
                fontWeight: 600,
                pointerEvents: "none",
                maxWidth: "80%",
              }}
            >
              <i className="bi bi-hand-index-thumb-fill" aria-hidden="true" style={{ color: "#2f6fed", fontSize: 15 }} />
              <span>Tocá una parte para cambiar su color</span>
            </div>
          ) : null}
          {canFlip ? (
            <div
              className={styles.previewPovSwitcher}
              onPointerDown={(event) => {
                event.stopPropagation();
              }}
            >
              <button
                type="button"
                className={`${styles.previewPovBtn} ${view === "front" ? styles.previewPovBtnActive : ""}`}
                onClick={(event) => {
                  event.preventDefault();
                  event.stopPropagation();
                  if (view !== "front") onToggleView?.();
                }}
                aria-label="Ver frente"
                title="Ver frente"
              >
                Frente
              </button>
              <button
                type="button"
                className={`${styles.previewPovBtn} ${view === "back" ? styles.previewPovBtnActive : ""}`}
                onClick={(event) => {
                  event.preventDefault();
                  event.stopPropagation();
                  if (view !== "back") onToggleView?.();
                }}
                aria-label="Ver espalda"
                title="Ver espalda"
              >
                Espalda
              </button>
            </div>
          ) : null}
          <button
            type="button"
            className={`${styles.previewCanvasControl} ${styles.previewCanvasControlReset}`}
            onPointerDown={(event) => {
              event.stopPropagation();
            }}
            onClick={(event) => {
              event.preventDefault();
              event.stopPropagation();
              onResetDesign?.();
            }}
            aria-label="Resetear diseño"
            title="Resetear diseño"
          >
            <span className={styles.previewCanvasResetIcon} aria-hidden="true">
              <i className="bi bi-trash3" />
            </span>
          </button>
          <div className={styles.previewCanvasNav}>
            <button
              type="button"
              className={`${styles.previewCanvasNavBtn} ${styles.previewCanvasNavBtnPrev}`}
              onPointerDown={(event) => {
                event.stopPropagation();
              }}
              onClick={(event) => {
                event.preventDefault();
                event.stopPropagation();
                onPrev?.();
              }}
              disabled={currentStep === 1}
              aria-label="Paso anterior"
              title="Paso anterior"
            >
              ‹ ANTERIOR
            </button>
            <button
              type="button"
              className={`${styles.previewCanvasNavBtn} ${styles.previewCanvasNavBtnNext}`}
              onPointerDown={(event) => {
                event.stopPropagation();
              }}
              onClick={(event) => {
                event.preventDefault();
                event.stopPropagation();
                if (currentStep < totalSteps) {
                  onNext?.();
                  return;
                }
                onSave?.();
              }}
              disabled={currentStep < totalSteps ? nextDisabled : (saving || saveDisabled)}
              aria-label={currentStep < totalSteps ? "Paso siguiente" : "Confirmar"}
              title={currentStep < totalSteps ? "Paso siguiente" : "Confirmar"}
            >
              {currentStep < totalSteps ? "SIGUIENTE ›" : (saving ? "…" : "CONFIRMAR ✓")}
            </button>
          </div>
        </div>
      </div>
    </section>
  );
};

export default PreviewSection;
